import React, { useState, useEffect, useMemo } from 'react';
import { CompleteKundali, KundaliChartData, Language, PlanetPosition, PlanetName, ZodiacSign } from '../types/kundali';
import { formatOrdinal } from '../utils/dateUtils';
import { 
  evaluateCompleteBhavaCondition, 
  CompleteBhavaConditionAssessment, 
  SaptaVargajaDignity 
} from '../utils/bhavaConditionEngine';
import { 
  identifyChartArchetype, 
  generateHumanizedBhavaReading 
} from '../utils/humanizedToneEngine';
import { 
  getPlanetFullName, 
  getPlanetShortCode, 
  getZodiacName,
  getDignityName 
} from '../utils/language';
import {
  EnhancedPlanetaryDignity,
  getPanchadhaMaitriCompound,
  getCompoundRelationshipHindi
} from '../utils/planetaryDignityEngine';
import { getFunctionalNature } from '../utils/paeEngine';
import { 
  Sparkles, 
  Shield, 
  Compass, 
  Star, 
  Eye, 
  Award, 
  Activity, 
  Zap, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2, 
  AlertTriangle, 
  Layers, 
  Flame, 
  BookOpen, 
  Scale, 
  CircleDot,
  MessageSquare,
  Sparkle,
  Crown,
  Users,
  BarChart2,
  ChevronRight,
  ShieldCheck,
  EyeOff,
  TrendingUp,
  Info,
  Heart,
  Smile,
  FileText
} from 'lucide-react';
import {
  PopoverWrapper,
  BhaveshaRelationshipHoverCard,
  NakshatraHoverCard,
  DignityHoverCard,
  ShadbalaHoverCard,
  BavHoverCard,
  DrishtiAspectHoverCard,
  D9NavamshaHoverCard
} from './PlanetaryHoverPopover';
import { PlanetaryBhaveshMatrixView } from './PlanetaryBhaveshMatrixView';
import { calculatePrastaraTables } from '../utils/ashtakvargaAdvancedEngine';
import {
  computeSynthesizedUpaya,
  computeLaymanPracticalSummary
} from './HouseInspectorView';

// Helpers for House Dynamics & Dignity Table
const getNaturalRelationship = (lagnaLord: string, planet: string) => {
  if (planet === 'Ascendant' || planet === 'Rahu' || planet === 'Ketu') return 'Neutral';
  if (lagnaLord === planet) return 'Self / Root';

  const friendships: Record<string, { friends: string[], enemies: string[] }> = {
    Sun: { friends: ['Moon', 'Mars', 'Jupiter'], enemies: ['Venus', 'Saturn'] },
    Moon: { friends: ['Sun', 'Mercury'], enemies: [] },
    Mars: { friends: ['Sun', 'Moon', 'Jupiter'], enemies: ['Mercury'] },
    Mercury: { friends: ['Sun', 'Venus'], enemies: ['Moon'] },
    Jupiter: { friends: ['Sun', 'Moon', 'Mars'], enemies: ['Mercury', 'Venus'] },
    Venus: { friends: ['Mercury', 'Saturn'], enemies: ['Sun', 'Moon'] },
    Saturn: { friends: ['Mercury', 'Venus'], enemies: ['Sun', 'Moon', 'Mars'] }
  };

  const lordRules = friendships[lagnaLord];
  if (!lordRules) return 'Neutral';
  if (lordRules.friends.includes(planet)) return 'Friend';
  if (lordRules.enemies.includes(planet)) return 'Enemy';
  return 'Neutral';
};

const getHouseKarakas = (house: number): string[] => {
  const karakaMap: Record<number, string[]> = {
    1: ['Sun', 'Mars'],
    2: ['Jupiter', 'Mercury'],
    3: ['Mars'],
    4: ['Moon', 'Venus', 'Mars'],
    5: ['Jupiter', 'Mercury'],
    6: ['Mars', 'Saturn'],
    7: ['Venus', 'Jupiter'],
    8: ['Saturn'],
    9: ['Jupiter', 'Sun'],
    10: ['Sun', 'Mercury', 'Saturn', 'Jupiter'],
    11: ['Jupiter'],
    12: ['Saturn', 'Ketu', 'Venus']
  };
  return karakaMap[house] || [];
};

const getBhaveshaRelationship = (
  planetName: string,
  signLordName: string,
  planetHouse: number,
  signLordHouse: number,
  currentHouseNumber?: number
) => {
  const isActualLord = planetName === signLordName;
  const isPhysicallyInThisHouse = currentHouseNumber ? planetHouse === currentHouseNumber : planetHouse === signLordHouse;

  if (isActualLord) {
    if (isPhysicallyInThisHouse) {
      return {
        compound: 'Own House',
        compoundEn: 'Lord in Own House',
        compoundHi: 'स्वक्षेत्री भावेश (स्वामी)',
        natural: 'Self',
        naturalHi: 'स्वयं',
        temporal: 'Self',
        temporalHi: 'स्वयं',
        isLord: true
      };
    } else {
      return {
        compound: 'Own Lord',
        compoundEn: `Lord placed in ${planetHouse}H`,
        compoundHi: `भावेश ${planetHouse} भाव में स्थित`,
        natural: 'Self',
        naturalHi: 'स्वयं',
        temporal: 'Self',
        temporalHi: 'स्वयं',
        isLord: true
      };
    }
  }
  if (planetName === 'Ascendant' || planetName === 'Rahu' || planetName === 'Ketu') {
    return {
      compound: 'Neutral',
      compoundEn: 'Chaya / Neutral',
      compoundHi: 'छाया / सम',
      natural: 'Neutral',
      naturalHi: 'सम',
      temporal: 'Neutral',
      temporalHi: 'सम',
      isLord: false
    };
  }

  const naturalRel = getNaturalRelationship(signLordName, planetName) as 'Friend' | 'Neutral' | 'Enemy';
  const houseDistance = ((planetHouse - signLordHouse + 12) % 12) + 1;
  const isTemporalFriend = [2, 3, 4, 10, 11, 12].includes(houseDistance);
  const temporalRel: 'Friend' | 'Enemy' = isTemporalFriend ? 'Friend' : 'Enemy';
  
  const compound = getPanchadhaMaitriCompound(naturalRel, temporalRel);
  const compoundHi = getCompoundRelationshipHindi(compound);
  
  return {
    compound,
    compoundEn: `${planetName} → ${signLordName}: ${compound}`,
    compoundHi: `${planetName} ➔ ${signLordName}: ${compoundHi}`,
    natural: naturalRel,
    naturalHi: naturalRel === 'Friend' ? 'मित्र' : naturalRel === 'Enemy' ? 'शत्रु' : 'सम',
    temporal: temporalRel,
    temporalHi: isTemporalFriend ? 'तात्कालिक मित्र' : 'तात्कालिक शत्रु',
    isLord: false
  };
};

const getExactDignity = (planet: string, signIndex: number, deg: number) => {
  if (planet === 'Ascendant') return 'Base Ascendant';
  
  const EXALTATIONS: { [key: string]: number } = { Sun: 0, Moon: 1, Mars: 9, Mercury: 5, Jupiter: 3, Venus: 11, Saturn: 6, Rahu: 1, Ketu: 7 };
  const DEBILITATIONS: { [key: string]: number } = { Sun: 6, Moon: 7, Mars: 3, Mercury: 11, Jupiter: 9, Venus: 5, Saturn: 0, Rahu: 7, Ketu: 1 };
  
  if (EXALTATIONS[planet] === signIndex) return 'Exalted (Uccha)';
  if (DEBILITATIONS[planet] === signIndex) return 'Debilitated (Neecha)';

  if (planet === 'Sun' && signIndex === 4 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Moon' && signIndex === 1 && deg > 3 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Mars' && signIndex === 0 && deg <= 12) return 'Moolatrikona';
  if (planet === 'Mercury' && signIndex === 5 && deg > 15 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Jupiter' && signIndex === 8 && deg <= 10) return 'Moolatrikona';
  if (planet === 'Venus' && signIndex === 6 && deg <= 15) return 'Moolatrikona';
  if (planet === 'Saturn' && signIndex === 10 && deg <= 20) return 'Moolatrikona';

  const ownSigns: Record<string, number[]> = {
    Sun: [4], Moon: [3], Mars: [0, 7], Mercury: [2, 5], Jupiter: [8, 11], Venus: [1, 6], Saturn: [9, 10]
  };
  
  if (ownSigns[planet]?.includes(signIndex)) return 'Own House (Swakshetra)';

  const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
  const signLord = ZODIAC_LORDS[signIndex];

  if (planet === 'Rahu' || planet === 'Ketu') return 'Neutral Sign';

  const rel = getNaturalRelationship(planet, signLord);
  if (rel === 'Friend') return 'Friendly Sign (Mitra)';
  if (rel === 'Enemy') return 'Enemy Sign (Shatru)';
  return 'Neutral Sign (Sama)';
};

// Zodiac Sign Elements & Modalities
const SIGN_TRAITS: Record<ZodiacSign, { elementEn: string; elementHi: string; modalityEn: string; modalityHi: string }> = {
  Aries: { elementEn: 'Fire (Agni)', elementHi: 'अग्नि तत्व', modalityEn: 'Movable (Chara)', modalityHi: 'चर राशि' },
  Taurus: { elementEn: 'Earth (Prithvi)', elementHi: 'पृथ्वी तत्व', modalityEn: 'Fixed (Sthira)', modalityHi: 'स्थिर राशि' },
  Gemini: { elementEn: 'Air (Vayu)', elementHi: 'वायु तत्व', modalityEn: 'Dual (Dwisvabhava)', modalityHi: 'द्विस्वभाव राशि' },
  Cancer: { elementEn: 'Water (Jala)', elementHi: 'जल तत्व', modalityEn: 'Movable (Chara)', modalityHi: 'चर राशि' },
  Leo: { elementEn: 'Fire (Agni)', elementHi: 'अग्नि तत्व', modalityEn: 'Fixed (Sthira)', modalityHi: 'स्थिर राशि' },
  Virgo: { elementEn: 'Earth (Prithvi)', elementHi: 'पृथ्वी तत्व', modalityEn: 'Dual (Dwisvabhava)', modalityHi: 'द्विस्वभाव राशि' },
  Libra: { elementEn: 'Air (Vayu)', elementHi: 'वायु तत्व', modalityEn: 'Movable (Chara)', modalityHi: 'चर राशि' },
  Scorpio: { elementEn: 'Water (Jala)', elementHi: 'जल तत्व', modalityEn: 'Fixed (Sthira)', modalityHi: 'स्थिर राशि' },
  Sagittarius: { elementEn: 'Fire (Agni)', elementHi: 'अग्नि तत्व', modalityEn: 'Dual (Dwisvabhava)', modalityHi: 'द्विस्वभाव राशि' },
  Capricorn: { elementEn: 'Earth (Prithvi)', elementHi: 'पृथ्वी तत्व', modalityEn: 'Movable (Chara)', modalityHi: 'चर राशि' },
  Aquarius: { elementEn: 'Air (Vayu)', elementHi: 'वायु तत्व', modalityEn: 'Fixed (Sthira)', modalityHi: 'स्थिर राशि' },
  Pisces: { elementEn: 'Water (Jala)', elementHi: 'जल तत्व', modalityEn: 'Dual (Dwisvabhava)', modalityHi: 'द्विस्वभाव राशि' }
};

// House Classical Goals & Categories
const HOUSE_CLASSIFICATIONS: Record<number, { aimEn: string; aimHi: string; categoryEn: string; categoryHi: string; dChart: string; dChartFocus: string; dChartFocusHi: string }> = {
  1: { aimEn: 'Dharma (Purpose & Identity)', aimHi: 'धर्म त्रिकोण (आत्म स्वरूप)', categoryEn: 'Kendra & Trikona (Supreme Center)', categoryHi: 'केंद्र एवं त्रिकोण', dChart: 'D-9 (Navamsha)', dChartFocus: 'Soul Destiny & Innate Dignity', dChartFocusHi: 'आत्मिक गरिमा एवं भाग्योदय' },
  2: { aimEn: 'Artha (Wealth & Family)', aimHi: 'अर्थ त्रिकोण (धन व परिवार)', categoryEn: 'Maraka & Panapara (Treasury)', categoryHi: 'मारक एवं धन स्थान', dChart: 'D-2 (Hora)', dChartFocus: 'Liquid Assets & Prosperity', dChartFocusHi: 'संपत्ति एवं समृद्धि' },
  3: { aimEn: 'Kama (Initiative & Desires)', aimHi: 'काम त्रिकोण (पराक्रम व इच्छा)', categoryEn: 'Upachaya (Growth via Effort)', categoryHi: 'उपचय (प्रयास से वृद्धि)', dChart: 'D-3 (Drekkana)', dChartFocus: 'Courage, Energy & Siblings', dChartFocusHi: 'साहस, ऊर्जा एवं सहोदर' },
  4: { aimEn: 'Moksha (Emotional Sanctuary)', aimHi: 'मोक्ष त्रिकोण (गृह सुख व शांति)', categoryEn: 'Kendra (Pillar of Happiness)', categoryHi: 'केंद्र (सुख स्तंभ)', dChart: 'D-4 / D-16', dChartFocus: 'Real Estate, Vehicles & Peace', dChartFocusHi: 'भूमि, वाहन एवं मातृ सुख' },
  5: { aimEn: 'Dharma (Intellect & Karma)', aimHi: 'धर्म त्रिकोण (बुद्धि व पुण्य)', categoryEn: 'Trikona (Lakshmi Sthana)', categoryHi: 'त्रिकोण (लक्ष्मी स्थान)', dChart: 'D-7 (Saptamsha)', dChartFocus: 'Progeny, Creativity & Mantras', dChartFocusHi: 'संतान, मेधा एवं पूर्व पुण्य' },
  6: { aimEn: 'Artha (Service & Defense)', aimHi: 'अर्थ त्रिकोण (रोग व शत्रु विजय)', categoryEn: 'Dusthana & Upachaya (Combat)', categoryHi: 'दुस्थान एवं उपचय', dChart: 'D-6 (Shashtamsha)', dChartFocus: 'Litigation, Debts & Immunity', dChartFocusHi: 'शत्रु बाधा, ऋण एवं स्वास्थ्य' },
  7: { aimEn: 'Kama (Partnership & Union)', aimHi: 'काम त्रिकोण (दांपत्य व संबंध)', categoryEn: 'Kendra & Maraka (Public Treaty)', categoryHi: 'केंद्र एवं मारक', dChart: 'D-9 (Navamsha)', dChartFocus: 'Spouse Chemistry & Marital Fruition', dChartFocusHi: 'जीवनसाथी, दांपत्य व साझेदारी' },
  8: { aimEn: 'Moksha (Transformation & Truth)', aimHi: 'मोक्ष त्रिकोण (आयु व अनुसंधान)', categoryEn: 'Dusthana / Randhra (Metamorphosis)', categoryHi: 'दुस्थान (गूढ़ रहस्य व रूपांतरण)', dChart: 'D-8 (Ashtamsha)', dChartFocus: 'Longevity, Unearned Wealth & Occult', dChartFocusHi: 'दीर्घायु, पैतृक लाभ व रहस्य' },
  9: { aimEn: 'Dharma (Fortune & Higher Grace)', aimHi: 'धर्म त्रिकोण (परम भाग्य व गुरु)', categoryEn: 'Supreme Trikona (Bhagya Sthana)', categoryHi: 'सर्वोच्च त्रिकोण (भाग्य स्थान)', dChart: 'D-9 / D-20', dChartFocus: 'Spiritual Grace & Higher Wisdom', dChartFocusHi: 'ईश्वरीय कृपा, गुरु एवं धर्म' },
  10: { aimEn: 'Artha (Karma & Executive Stature)', aimHi: 'अर्थ त्रिकोण (कर्म व राज्य सत्ता)', categoryEn: 'Supreme Kendra (Midheaven Zenith)', categoryHi: 'सर्वोच्च केंद्र (दशम कर्म भाव)', dChart: 'D-10 (Dashamsha)', dChartFocus: 'Career Leadership & Public Renown', dChartFocusHi: 'कार्यक्षेत्र, पद-प्रतिष्ठा एवं आजीविका' },
  11: { aimEn: 'Kama (Gains & Fulfillment)', aimHi: 'काम त्रिकोण (आय व सिद्धि)', categoryEn: 'Supreme Upachaya (Abundant Labha)', categoryHi: 'सर्वोच्च उपचय (लाभ स्थान)', dChart: 'D-11 (Rudramsha)', dChartFocus: 'Revenue Streams & Major Desires', dChartFocusHi: 'आय के स्रोत, मित्र व सफलता' },
  12: { aimEn: 'Moksha (Liberation & Solitude)', aimHi: 'मोक्ष त्रिकोण (त्याग व विदेश)', categoryEn: 'Dusthana & Moksha (Sanctuary)', categoryHi: 'दुस्थान (मोक्ष व विदेश)', dChart: 'D-12 (Dwadashamsha)', dChartFocus: 'Foreign Horizons & Spiritual Peace', dChartFocusHi: 'विदेश गमन, दान व आत्म शांति' }
};

// Sthira House Karakas
const HOUSE_KARAKAS_MAP: Record<number, { fixed: PlanetName[]; primary: PlanetName; noteEn: string; noteHi: string }> = {
  1: { fixed: ['Sun', 'Mars'], primary: 'Sun', noteEn: 'Sun governs Vitality & Soul (Atma); Mars governs Physical Energy (Sharira).', noteHi: 'सूर्य आत्मा व जीवनशक्ति का कारक है; मंगल शारीरिक ऊर्जा का।' },
  2: { fixed: ['Jupiter', 'Mercury'], primary: 'Jupiter', noteEn: 'Jupiter governs Treasury & Family; Mercury governs Eloquent Speech.', noteHi: 'गुरु संचित धन व कुल का कारक है; बुध वाणी व गणना का।' },
  3: { fixed: ['Mars'], primary: 'Mars', noteEn: 'Mars is the sole Karaka for Courage (Parakrama) and Younger Siblings.', noteHi: 'मंगल पराक्रम, बाहुबल और अनुजों का मुख्य कारक है।' },
  4: { fixed: ['Moon', 'Venus', 'Mars', 'Mercury'], primary: 'Moon', noteEn: 'Moon governs Mother & Peace; Venus for Vehicles/Luxury; Mars for Land/Property.', noteHi: 'चंद्र मातृ सुख व मन का; शुक्र वाहन व विलासिता का; मंगल भूमि का कारक है।' },
  5: { fixed: ['Jupiter', 'Mercury'], primary: 'Jupiter', noteEn: 'Jupiter governs Progeny (Putra) & Wisdom (Dhi); Mercury governs Advisory Intellect.', noteHi: 'गुरु संतान व प्रज्ञा का कारक है; बुध विश्लेषणात्मक मेधा का।' },
  6: { fixed: ['Mars', 'Saturn'], primary: 'Mars', noteEn: 'Mars crushes Rivals; Saturn signifies Diseases & Chronic Service.', noteHi: 'मंगल शत्रुहंता है; शनि रोग, ऋण और सेवा का कारक है।' },
  7: { fixed: ['Venus', 'Jupiter'], primary: 'Venus', noteEn: 'Venus governs Spousal Love & Art; Jupiter governs Marital Dignity & Ethics.', noteHi: 'शुक्र दांपत्य प्रेम व सुख का कारक है; गुरु वैवाहिक मर्यादा का।' },
  8: { fixed: ['Saturn'], primary: 'Saturn', noteEn: 'Saturn is the primary Ayush Karaka (Signifier of Longevity & Endurance).', noteHi: 'शनि परम आयु कारक (दीर्घायु और धैर्य) है।' },
  9: { fixed: ['Jupiter', 'Sun'], primary: 'Jupiter', noteEn: 'Jupiter governs Divine Grace (Bhagya) & Gurus; Sun signifies Father & Ancestral Dharma.', noteHi: 'गुरु ईश्वरीय कृपा व गुरु का कारक है; सूर्य पिता व धर्म का।' },
  10: { fixed: ['Sun', 'Mercury', 'Saturn', 'Jupiter'], primary: 'Sun', noteEn: 'Sun signifies Authority & State; Mercury for Commerce; Saturn for Hard Work; Jupiter for Ethics.', noteHi: 'सूर्य पद-प्रतिष्ठा का; बुध व्यापार का; शनि श्रम का; गुरु नीति का कारक है।' },
  11: { fixed: ['Jupiter'], primary: 'Jupiter', noteEn: 'Jupiter is the universal Karaka for Abundant Inflows & Fulfilled Aspirations.', noteHi: 'गुरु प्रचुर आर्थिक लाभ और मनोकामना सिद्धि का कारक है।' },
  12: { fixed: ['Saturn', 'Ketu', 'Venus'], primary: 'Saturn', noteEn: 'Saturn signifies Solitude & Loss; Ketu for Spiritual Moksha; Venus for Comforts.', noteHi: 'शनि व्यय व एकांत का; केतु मोक्ष का; शुक्र शयन सुख का कारक है।' }
};

// Calculate Baladi Avastha based on planet degree and odd/even sign
const getBaladiAvastha = (degreeInSign: number, isOddSign: boolean) => {
  const d = Math.max(0, Math.min(29.99, degreeInSign));
  const stage = Math.floor(d / 6); // 0 to 4
  
  if (isOddSign) {
    if (stage === 0) return { nameEn: 'Bala (Infant)', nameHi: 'बाल्यावस्था', potencyPct: 25, badgeColor: 'amber' };
    if (stage === 1) return { nameEn: 'Kumara (Youth)', nameHi: 'कुमारावस्था', potencyPct: 50, badgeColor: 'teal' };
    if (stage === 2) return { nameEn: 'Yuva (Prime Adult)', nameHi: 'युवावस्था', potencyPct: 100, badgeColor: 'emerald' };
    if (stage === 3) return { nameEn: 'Vriddha (Aged)', nameHi: 'वृद्धावस्था', potencyPct: 20, badgeColor: 'amber' };
    return { nameEn: 'Mrita (Dormant)', nameHi: 'मृतावस्था', potencyPct: 0, badgeColor: 'rose' };
  } else {
    // Even signs are reversed
    if (stage === 0) return { nameEn: 'Mrita (Dormant)', nameHi: 'मृतावस्था', potencyPct: 0, badgeColor: 'rose' };
    if (stage === 1) return { nameEn: 'Vriddha (Aged)', nameHi: 'वृद्धावस्था', potencyPct: 20, badgeColor: 'amber' };
    if (stage === 2) return { nameEn: 'Yuva (Prime Adult)', nameHi: 'युवावस्था', potencyPct: 100, badgeColor: 'emerald' };
    if (stage === 3) return { nameEn: 'Kumara (Youth)', nameHi: 'कुमारावस्था', potencyPct: 50, badgeColor: 'teal' };
    return { nameEn: 'Bala (Infant)', nameHi: 'बाल्यावस्था', potencyPct: 25, badgeColor: 'amber' };
  }
};

interface UnifiedBhavaConditionAssessmentViewProps {
  houseNumber: number;
  chartData: KundaliChartData;
  kundali: CompleteKundali;
  lang?: Language;
  shadbalaData?: any;
  enhancedDignityMap?: Record<string, EnhancedPlanetaryDignity>;
  onSelectHouse?: (houseNumber: number) => void;
  onSelectPlanet?: (planet: any) => void;
}

export const UnifiedBhavaConditionAssessmentView: React.FC<UnifiedBhavaConditionAssessmentViewProps> = ({
  houseNumber,
  chartData,
  kundali,
  lang = 'en',
  shadbalaData,
  enhancedDignityMap,
  onSelectHouse,
  onSelectPlanet
}) => {
  const activeLang = (lang as Language) || 'en';
  const isHi = activeLang === 'hi';

  const [activeTab, setActiveTab] = useState<'TRIPOD' | 'ADVANCED' | 'SYNTHESIS' | 'HUMANIZED'>('HUMANIZED');
  const [isDignityTableOpen, setIsDignityTableOpen] = useState<boolean>(true);
  const [tableTooltipsEnabled, setTableTooltipsEnabled] = useState<boolean>(true);
  const [matrixFilter, setMatrixFilter] = useState<'ALL' | 'OCCUPANT' | 'LORD' | 'ASPECT' | 'BENEFIC'>('ALL');

  const prastaraTables = useMemo(() => calculatePrastaraTables(kundali), [kundali]);
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    return localStorage.getItem('astro_inspector_default_view') === 'expanded';
  });

  useEffect(() => {
    const handlePrefChange = () => {
      const pref = localStorage.getItem('astro_inspector_default_view');
      setIsExpanded(pref === 'expanded');
    };
    window.addEventListener('astro_inspector_view_pref_changed', handlePrefChange);
    return () => window.removeEventListener('astro_inspector_view_pref_changed', handlePrefChange);
  }, []);

  const assessment: CompleteBhavaConditionAssessment = evaluateCompleteBhavaCondition(
    houseNumber,
    chartData,
    kundali
  );

  const archetype = identifyChartArchetype(kundali);
  const humanizedReading = generateHumanizedBhavaReading(assessment, archetype);

  const { meta, headerSummary, triPillar, advancedVedicMetrics, synthesisAndActivation } = assessment;

  // Comprehensive Astrological Synthesis Attributes
  const houseSign = (triPillar.vessel.sign || 'Aries') as ZodiacSign;
  const signTrait = SIGN_TRAITS[houseSign] || SIGN_TRAITS.Aries;
  const houseClass = HOUSE_CLASSIFICATIONS[houseNumber] || HOUSE_CLASSIFICATIONS[1];
  const karakaInfo = HOUSE_KARAKAS_MAP[houseNumber] || HOUSE_KARAKAS_MAP[1];

  // Occupants in this house
  const occupants = useMemo(() => {
    return (chartData.planets || []).filter(p => p.house === houseNumber && p.name !== 'Ascendant');
  }, [chartData.planets, houseNumber]);

  // House Lord Planet details
  const houseLordName = triPillar.manager.lordPlanet as PlanetName;
  const lordPlanet = useMemo(() => {
    return (chartData.planets || []).find(p => p.name === houseLordName);
  }, [chartData.planets, houseLordName]);

  const lordEnhanced = enhancedDignityMap?.[houseLordName];
  const lordShadbala = shadbalaData?.planets?.find((s: any) => s.planet === houseLordName);

  // Lord Dispositor and Panchadha Maitri
  const lordDispositorName = lordPlanet ? (chartData.planets.find(p => p.house === lordPlanet.house)?.signLord || lordPlanet.signLord) : null;
  const lordDispositorRel = useMemo(() => {
    if (!lordPlanet || !lordDispositorName) return { compound: 'Own House', compoundEn: 'In Own Sign (Lord)', compoundHi: 'स्वराशि में स्थित' };
    if (lordPlanet.name === lordDispositorName) return { compound: 'Own House', compoundEn: 'In Own Sign (Lord)', compoundHi: 'स्वराशि में स्थित' };

    const naturalFriendships: Record<string, { friends: string[], enemies: string[] }> = {
      Sun: { friends: ['Moon', 'Mars', 'Jupiter'], enemies: ['Venus', 'Saturn'] },
      Moon: { friends: ['Sun', 'Mercury'], enemies: [] },
      Mars: { friends: ['Sun', 'Moon', 'Jupiter'], enemies: ['Mercury'] },
      Mercury: { friends: ['Sun', 'Venus'], enemies: ['Moon'] },
      Jupiter: { friends: ['Sun', 'Moon', 'Mars'], enemies: ['Mercury', 'Venus'] },
      Venus: { friends: ['Mercury', 'Saturn'], enemies: ['Sun', 'Moon'] },
      Saturn: { friends: ['Mercury', 'Venus'], enemies: ['Sun', 'Moon', 'Mars'] }
    };

    const hostRules = naturalFriendships[lordDispositorName] || { friends: [], enemies: [] };
    const naturalRel: 'Friend' | 'Neutral' | 'Enemy' = hostRules.friends.includes(lordPlanet.name)
      ? 'Friend'
      : hostRules.enemies.includes(lordPlanet.name)
      ? 'Enemy'
      : 'Neutral';

    const dispositorPos = chartData.planets.find(p => p.name === lordDispositorName);
    const dispositorHouse = dispositorPos?.house || 1;
    const houseDistance = ((lordPlanet.house - dispositorHouse + 12) % 12) + 1;
    const isTemporalFriend = [2, 3, 4, 10, 11, 12].includes(houseDistance);
    const temporalRel = isTemporalFriend ? 'Friend' : 'Enemy';

    const compound = getPanchadhaMaitriCompound(naturalRel, temporalRel);
    const compoundHi = getCompoundRelationshipHindi(compound);

    return { compound, compoundEn: compound, compoundHi };
  }, [lordPlanet, lordDispositorName, chartData.planets]);

  // Lord Baladi Avastha
  const isLordSignOdd = lordPlanet ? [0, 2, 4, 6, 8, 10].includes(lordPlanet.signIndex ?? 0) : true;
  const lordAvastha = lordPlanet ? getBaladiAvastha(lordPlanet.degreeInSign ?? 15, isLordSignOdd) : { nameEn: 'Yuva', nameHi: 'युवावस्था', potencyPct: 100, badgeColor: 'emerald' };

  // SAV & BAV points
  const savPoints = (kundali.ashtakvarga as any)?.sarvashtakavarga?.[houseNumber - 1] ?? (headerSummary as any)?.savPoints ?? 28;

  // Functional Role for Lagna
  const ascSign = chartData.planets?.find(p => p.name === 'Ascendant')?.sign || 'Aries';
  const lordFunctional = (houseLordName !== 'Rahu' && houseLordName !== 'Ketu')
    ? getFunctionalNature(ascSign as any, houseLordName as any).nature
    : 'Karmic Proxy';

  // Conjunction Dynamics in this House
  const conjunctionDynamics = useMemo(() => {
    if (occupants.length <= 1) return null;

    const pairs: Array<{
      p1: PlanetPosition;
      p2: PlanetPosition;
      diffDegrees: number;
      isPlanetaryWar: boolean;
      isCombust: boolean;
      chemistry: string;
      chemistryHi: string;
      isFriendly: boolean;
    }> = [];

    for (let i = 0; i < occupants.length; i++) {
      for (let j = i + 1; j < occupants.length; j++) {
        const p1 = occupants[i];
        const p2 = occupants[j];
        const diff = Math.abs((p1.degreeInSign ?? 0) - (p2.degreeInSign ?? 0));
        const isPlanetaryWar = (p1.name !== 'Sun' && p1.name !== 'Moon' && p1.name !== 'Rahu' && p1.name !== 'Ketu' &&
                                p2.name !== 'Sun' && p2.name !== 'Moon' && p2.name !== 'Rahu' && p2.name !== 'Ketu' &&
                                diff <= 1.0);
        const isCombust = (p1.name === 'Sun' || p2.name === 'Sun') && diff <= 6.0;

        const naturalFriends: Record<string, string[]> = {
          Sun: ['Moon', 'Mars', 'Jupiter'],
          Moon: ['Sun', 'Mercury'],
          Mars: ['Sun', 'Moon', 'Jupiter'],
          Mercury: ['Sun', 'Venus'],
          Jupiter: ['Sun', 'Moon', 'Mars'],
          Venus: ['Mercury', 'Saturn'],
          Saturn: ['Mercury', 'Venus']
        };

        const isFriendly = Boolean(naturalFriends[p1.name]?.includes(p2.name) || naturalFriends[p2.name]?.includes(p1.name));
        const chemistry = isFriendly ? 'Harmonious Alliance (Mitra Yuti)' : 'Competing Energies (Friction)';
        const chemistryHi = isFriendly ? 'मित्र युति (सामंजस्यपूर्ण योग)' : 'तनावपूर्ण युति (द्वंद्व)';

        pairs.push({
          p1,
          p2,
          diffDegrees: diff,
          isPlanetaryWar,
          isCombust,
          chemistry,
          chemistryHi,
          isFriendly
        });
      }
    }
    return pairs;
  }, [occupants]);

  // Jaimini Chara Karakas
  const charaKarakas = useMemo(() => {
    const validPlanets = (chartData.planets || [])
      .filter(p => !['Ascendant', 'Rahu', 'Ketu'].includes(p.name))
      .map(p => ({ name: p.name, deg: p.degreeInSign ?? 0 }))
      .sort((a, b) => b.deg - a.deg);

    const labels = [
      { key: 'AK', nameEn: 'Atmakaraka (Soul / Identity)', nameHi: 'आत्मकारक (आत्मा/स्वरूप)' },
      { key: 'AmK', nameEn: 'Amatyakaraka (Career / Action)', nameHi: 'अमात्यकारक (कर्म/बुद्धि)' },
      { key: 'BK', nameEn: 'Bhratrukaraka (Courage / Guru)', nameHi: 'भ्रातृकारक (पराक्रम/गुरु)' },
      { key: 'MK', nameEn: 'Matrukaraka (Sanctuary / Mother)', nameHi: 'मातृकारक (माता/सुख)' },
      { key: 'PK', nameEn: 'Putrakaraka (Intellect / Legacy)', nameHi: 'पुत्रकारक (संतान/ज्ञान)' },
      { key: 'GK', nameEn: 'Gnatikaraka (Obstacles / Defense)', nameHi: 'ज्ञातिरक (बाधा/संघर्ष)' },
      { key: 'DK', nameEn: 'Darakaraka (Partnership / Spouse)', nameHi: 'दारकारक (जीवनसाथी/संबंध)' }
    ];

    const map: Record<string, { key: string; nameEn: string; nameHi: string }> = {};
    validPlanets.forEach((p, idx) => {
      if (labels[idx]) {
        map[p.name] = labels[idx];
      }
    });
    return map;
  }, [chartData.planets]);

  // Primary Karaka Planet
  const primaryKarakaName = triPillar.deliverable.naisargikaKaraka;
  const karakaPlanet = chartData.planets?.find(p => p.name === primaryKarakaName);
  const karakaShadbala = shadbalaData?.planets?.find((s: any) => s.planet === primaryKarakaName);
  const karakaChara = charaKarakas[primaryKarakaName];
  const lordChara = charaKarakas[houseLordName];

  // Practical Summary & Remedies calculations for Tab 4
  const now = Date.now();
  const currentMD = kundali.dashas?.find((d: any) => d.isCurrent) || kundali.dashas?.[0];
  const currentAD = (currentMD as any)?.subDashas?.find((a: any) => a.isCurrent) || (currentMD as any)?.subDashas?.[0] || (currentMD as any)?.antardashas?.find((a: any) => a.isCurrent);
  const dashaActive = currentMD?.planet === houseLordName || currentAD?.planet === houseLordName;

  const laymanPracticalSummary = useMemo(() => {
    return computeLaymanPracticalSummary(
      houseNumber,
      triPillar.vessel.sign || 'Aries',
      houseLordName,
      occupants,
      triPillar.vessel.inboundDrishti || [],
      null,
      dashaActive,
      currentMD,
      currentAD,
      null,
      ascSign,
      isHi
    );
  }, [houseNumber, triPillar.vessel.sign, houseLordName, occupants, triPillar.vessel.inboundDrishti, dashaActive, currentMD, currentAD, ascSign, isHi]);

  const remedies = useMemo(() => {
    return computeSynthesizedUpaya(
      houseNumber,
      triPillar.vessel.sign || 'Aries',
      houseLordName,
      occupants,
      triPillar.vessel.inboundDrishti || [],
      laymanPracticalSummary.netScore,
      currentMD?.planet,
      currentAD?.planet,
      null,
      isHi
    );
  }, [houseNumber, triPillar.vessel.sign, houseLordName, occupants, triPillar.vessel.inboundDrishti, laymanPracticalSummary.netScore, currentMD, currentAD, isHi]);

  const getVitalityBadgeClass = (vitality: string) => {
    switch (vitality) {
      case 'Exemplary (Purna)':
        return 'bg-emerald-100 text-emerald-900 border-emerald-300';
      case 'Strong (Bala)':
        return 'bg-teal-100 text-teal-900 border-teal-300';
      case 'Moderate (Madhyama)':
        return 'bg-astro-secondary/15 text-astro-secondary border-[#E2DED5]';
      case 'Afflicted (Peedita)':
        return 'bg-rose-100 text-rose-900 border-rose-300';
      case 'Mixed (Mishra)':
      default:
        return 'bg-astro-card text-astro-secondary-text border-astro-border';
    }
  };

  const getDignityBadgeClass = (dignity: string) => {
    if (dignity.includes('Exalted') || dignity.includes('Moola-Trikona') || dignity.includes('Own Sign')) {
      return 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold';
    }
    if (dignity.includes('Friend')) {
      return 'bg-teal-50 text-teal-800 border-teal-300';
    }
    if (dignity.includes('Debilitated')) {
      return 'bg-rose-50 text-rose-800 border-rose-300 font-bold';
    }
    if (dignity.includes('Enemy')) {
      return 'bg-astro-secondary-surface text-astro-secondary border-[#E2DED5]';
    }
    return 'bg-astro-card text-astro-secondary-text border-astro-border';
  };

  return (
    <div className="bg-astro-bg border-2 border-astro-primary/35 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4">
      {/* 1. Header & Summary Metric */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-4">
        <div className="flex items-start sm:items-center gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-bold text-astro-primary text-base sm:text-lg font-serif">
                {meta.sanskritName} • {getZodiacName(triPillar.vessel.sign, activeLang)} (Lord: {triPillar.manager.lordPlanet})
              </h3>
              <span className={`text-[11px] px-2.5 py-0.5 rounded-full border ${getVitalityBadgeClass(headerSummary.netVitality)} font-semibold`}>
                {headerSummary.netVitality}
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded-md bg-astro-primary/10 text-astro-primary font-mono font-bold">
                {headerSummary.compositeScorePct}% Vitality
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {meta.englishTitle} • Lord: <span className="font-medium text-astro-primary">{triPillar.manager.lordPlanet}</span> • <span className="font-medium text-astro-primary">{headerSummary.houseArchetype}</span>
            </p>
          </div>
        </div>

        {/* BAV and SAV Benchmark Pill */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <div className="bg-astro-card px-3 py-1.5 rounded-xl border border-astro-accent/30 text-right">
            <span className="text-[10px] text-astro-secondary-text block font-semibold uppercase">Ashtakavarga BAV/SAV</span>
            <span className="text-xs font-mono font-bold text-astro-accent">
              {headerSummary.bavBinduCount} / 28 Baseline
            </span>
          </div>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-2.5 py-1.5 bg-astro-card hover:bg-astro-border rounded-xl text-astro-primary transition-colors border border-astro-border flex items-center gap-1.5 text-[11px] font-semibold"
            title={isExpanded ? 'Collapse Detailed Parashari View' : 'Expand Detailed Parashari View'}
          >
            <span>{isExpanded ? (isHi ? 'संक्षिप्त करें' : 'Collapse') : (isHi ? 'विस्तार करें' : 'Expand')}</span>
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-astro-primary" /> : <ChevronDown className="w-3.5 h-3.5 text-astro-primary" />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* House Vitality Gauge & Active Dasha Banner & Quick Links Bar */}
          <div className="space-y-3">
            {/* House Vitality Gauge (Net Energy Bar) */}
            <div className="p-3.5 bg-gradient-to-r from-indigo-900/90 via-astro-primary to-indigo-950 text-white rounded-xl shadow-xs space-y-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="font-bold tracking-wider uppercase font-serif text-[11px] text-indigo-100">
                    {isHi ? `भाव ${houseNumber} ऊर्जा एवं बल गेज (House Vitality Gauge)` : `House ${houseNumber} Net Energy Gauge`}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-mono">
                  <span className="bg-white/15 px-2 py-0.5 rounded text-indigo-100">
                    SAV: <b>{savPoints}/56</b>
                  </span>
                  <span className="bg-emerald-500/30 text-emerald-200 border border-emerald-400/30 px-2 py-0.5 rounded font-bold">
                    {headerSummary.compositeScorePct}% {isHi ? 'समग्र क्षमता' : 'Composite Capacity'}
                  </span>
                </div>
              </div>

              {/* Progress Gauge Meter Bar */}
              <div className="space-y-1">
                <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden flex">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      headerSummary.compositeScorePct >= 70 ? 'bg-gradient-to-r from-emerald-400 to-teal-300' :
                      headerSummary.compositeScorePct >= 50 ? 'bg-gradient-to-r from-amber-400 to-yellow-300' :
                      'bg-gradient-to-r from-rose-500 to-amber-400'
                    }`} 
                    style={{ width: `${Math.min(100, Math.max(10, headerSummary.compositeScorePct))}%` }}
                  />
                </div>
                <div className="flex justify-between items-center text-[9px] text-indigo-200 font-medium">
                  <span>{isHi ? 'तनाव / संघर्ष' : 'Friction / Strain'}</span>
                  <span>{isHi ? 'संतुलन (Madhyama)' : 'Balanced Capacity'}</span>
                  <span>{isHi ? 'पूर्ण फलीभूत (Purna)' : 'Exemplary Fulfillment'}</span>
                </div>
              </div>

              {/* Key Indicators Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 text-[10px]">
                <div className="bg-white/10 p-1.5 rounded-lg border border-white/10">
                  <span className="text-indigo-200 block text-[9px] uppercase">{isHi ? 'भाव स्थिति (Vitality)' : 'Vitality Rating'}</span>
                  <strong className="text-white text-[10.5px]">{headerSummary.netVitality.split(' ')[0]}</strong>
                </div>
                <div className="bg-white/10 p-1.5 rounded-lg border border-white/10">
                  <span className="text-indigo-200 block text-[9px] uppercase">{isHi ? 'दशा सक्रियता (Dasha)' : 'Dasha Activation'}</span>
                  <strong className={dashaActive ? 'text-emerald-300 font-bold' : 'text-indigo-200'}>
                    {dashaActive ? (isHi ? '⚡ सक्रिय (Active)' : '⚡ Active') : (isHi ? 'निष्क्रिय (Latent)' : 'Latent')}
                  </strong>
                </div>
                <div className="bg-white/10 p-1.5 rounded-lg border border-white/10">
                  <span className="text-indigo-200 block text-[9px] uppercase">{isHi ? 'सुरक्षा (Drishti / Kartari)' : 'Protection Level'}</span>
                  <strong className="text-teal-200">
                    {triPillar.vessel.inboundDrishti.some(a => a.isBenefic) ? (isHi ? '🛡️ शुभ रक्षित' : '🛡️ Benefic Shield') : (isHi ? 'सामान्य' : 'Neutral')}
                  </strong>
                </div>
                <div className="bg-white/10 p-1.5 rounded-lg border border-white/10">
                  <span className="text-indigo-200 block text-[9px] uppercase">{isHi ? 'भावेश BAV बल' : 'Lord BAV Potency'}</span>
                  <strong className="text-amber-200 font-mono">
                    {(() => {
                      const lordSignIdx = kundali.houses?.find(h => h.houseNumber === houseNumber)?.signIndex ?? 0;
                      const score = prastaraTables[houseLordName]?.signTotals?.[lordSignIdx];
                      return score !== undefined ? `${score}/8 Bindus` : 'N/A';
                    })()}
                  </strong>
                </div>
              </div>
            </div>

            {/* Live Active Dasha Status Banner */}
            {(() => {
              const mdPlanet = currentMD?.planet;
              const adPlanet = currentAD?.planet;
              const isMdMatch = mdPlanet && (mdPlanet === houseLordName || occupants.some(o => o.name === mdPlanet) || triPillar.vessel.inboundDrishti.some(a => a.planet === mdPlanet));
              const isAdMatch = adPlanet && (adPlanet === houseLordName || occupants.some(o => o.name === adPlanet) || triPillar.vessel.inboundDrishti.some(a => a.planet === adPlanet));

              if (!isMdMatch && !isAdMatch) return null;

              return (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-800 rounded-xl flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-600"></span>
                    </span>
                    <div>
                      <span className="font-bold text-emerald-900 dark:text-emerald-200 block text-[11px]">
                        {isHi ? '⚡ दशा सक्रियता संदेश (Live Dasha Focus):' : '⚡ Live Active Dasha Highlight:'}
                      </span>
                      <p className="text-[10.5px] text-emerald-950 dark:text-emerald-100">
                        {isHi
                          ? `वर्तमान दशा (${getPlanetFullName(mdPlanet as any, activeLang)} MD / ${getPlanetFullName(adPlanet as any, activeLang)} AD) भाव ${houseNumber} के परिणाम प्रत्यक्ष रूप से संचालित कर रही है।`
                          : `Currently operating ${mdPlanet} (MD) - ${adPlanet} (AD) period directly activates House ${houseNumber}.`}
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded bg-emerald-200 text-emerald-950 dark:bg-emerald-800 dark:text-emerald-100 text-[10px] font-bold shrink-0">
                    {isHi ? 'समय चक्र सक्रिय' : 'Timing Active'}
                  </span>
                </div>
              );
            })()}

            {/* Bhavat Bhavam & Argala Interconnected Quick-Links */}
            {onSelectHouse && (
              <div className="flex flex-wrap items-center gap-1.5 p-2 bg-astro-card rounded-xl border border-astro-border text-[11px]">
                <span className="font-bold text-astro-primary text-[10px] uppercase tracking-wider shrink-0 flex items-center gap-1">
                  <Compass className="w-3 h-3 text-astro-accent" />
                  {isHi ? 'संबंधित भाव क्विक-जंप (Quick Links):' : 'House Rel. Quick-Links:'}
                </span>

                {/* Bhavat Bhavam Button */}
                {advancedVedicMetrics.bhavatBhavam.reflectedHouseNumber && (
                  <button
                    type="button"
                    onClick={() => onSelectHouse(advancedVedicMetrics.bhavatBhavam.reflectedHouseNumber)}
                    className="px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200 font-semibold hover:bg-indigo-100 transition-colors flex items-center gap-1 text-[10.5px]"
                    title={isHi ? 'भावत भावम् सिद्धांत के तहत प्रतिबिंब भाव पर जाएँ' : 'Jump to Bhavat Bhavam reflected house'}
                  >
                    <span>🔗 Bhavat Bhavam (H{advancedVedicMetrics.bhavatBhavam.reflectedHouseNumber})</span>
                  </button>
                )}

                {/* Argala Interventions */}
                <span className="text-astro-secondary-text text-[10px] mx-0.5">|</span>
                <span className="text-[10px] font-semibold text-astro-secondary-text">{isHi ? 'अर्गला प्रभाव:' : 'Argala:'}</span>
                {[2, 4, 11].map((offset) => {
                  const targetH = ((houseNumber - 1 + offset - 1) % 12) + 1;
                  return (
                    <button
                      key={offset}
                      type="button"
                      onClick={() => onSelectHouse(targetH)}
                      className="px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800 text-teal-900 dark:text-teal-200 text-[10px] font-mono font-bold hover:bg-teal-100"
                      title={isHi ? `भाव ${targetH} (${offset === 2 ? 'धनार्गला' : offset === 4 ? 'सुखार्गला' : 'लाभार्गला'}) पर जाएँ` : `Jump to House ${targetH}`}
                    >
                      H{targetH} ({offset}th)
                    </button>
                  );
                })}

                {/* Virodha Argala */}
                <button
                  type="button"
                  onClick={() => onSelectHouse(((houseNumber - 1 + 11) % 12) + 1)}
                  className="px-1.5 py-0.5 rounded bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-900 dark:text-rose-200 text-[10px] font-mono font-bold hover:bg-rose-100"
                  title={isHi ? 'विरोधागला भाव (12th offset) पर जाएँ' : 'Jump to Virodha Argala house'}
                >
                  🛡️ Virodha (H{((houseNumber - 1 + 11) % 12) + 1})
                </button>
              </div>
            )}
          </div>

          {/* Executive Summary Callout */}
          <div className="p-3 bg-gradient-to-r from-astro-card to-astro-bg rounded-xl border border-astro-border text-xs text-astro-text flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-astro-accent shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold text-astro-primary block uppercase text-[10px] tracking-wider">
                {isHi ? 'एकीकृत पराशरी फलादेश सारांश' : 'Unified Parashari Synthesis'}
              </span>
              <p className="text-[11px] leading-relaxed text-astro-secondary-text">
                {headerSummary.executiveSummary}
              </p>
            </div>
          </div>

          {/* Pratyantar & Dasha House Activation Engine (Parashari Timing & Logic Breakdown) - Prominently Displayed First */}
          {(() => {
            const now = Date.now();
            const getSubList = (node: any) => node?.antardashas || node?.subDashas || [];
            const getNodePlanet = (node: any) => node?.planet || node?.graha;

            const mdRaw = kundali.dashas?.find((d: any) => d.isCurrent) || 
                          kundali.dashas?.find((d: any) => {
                            const s = d.startDate ? new Date(d.startDate).getTime() : 0;
                            const e = d.endDate ? new Date(d.endDate).getTime() : Infinity;
                            return now >= s && now <= e;
                          }) || 
                          kundali.dashas?.[0];

            const adList = getSubList(mdRaw);
            const adRaw = adList.find((a: any) => a.isCurrent) || 
                          adList.find((a: any) => {
                            const s = a.startDate ? new Date(a.startDate).getTime() : 0;
                            const e = a.endDate ? new Date(a.endDate).getTime() : Infinity;
                            return now >= s && now <= e;
                          }) || 
                          adList[0];

            const pdList = getSubList(adRaw);
            const pdRaw = pdList.find((p: any) => p.isCurrent) || 
                          pdList.find((p: any) => {
                            const s = p.startDate ? new Date(p.startDate).getTime() : 0;
                            const e = p.endDate ? new Date(p.endDate).getTime() : Infinity;
                            return now >= s && now <= e;
                          }) || 
                          pdList[0];

            const currentMD = mdRaw ? { ...mdRaw, planet: getNodePlanet(mdRaw) } : null;
            const currentAD = adRaw ? { ...adRaw, planet: getNodePlanet(adRaw) } : null;
            const currentPD = pdRaw ? { ...pdRaw, planet: getNodePlanet(pdRaw) } : null;

            const ascIndex = chartData.ascendantIndex ?? 0;
            const signIndex = (ascIndex + houseNumber - 1) % 12;
            const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
            const signLord = ZODIAC_LORDS[signIndex];
            const occupants = (chartData.planets || []).filter((p: any) => p.house === houseNumber).map((p: any) => p.name);
            const inboundDrishti = triPillar?.vessel?.inboundDrishti || [];
            const aspectingPlanets = inboundDrishti.map((d: any) => d.planet);
            
            const NATURAL_KARAKAS: Record<number, string> = {
              1: 'Sun', 2: 'Jupiter', 3: 'Mars', 4: 'Moon', 5: 'Jupiter', 6: 'Saturn',
              7: 'Venus', 8: 'Saturn', 9: 'Jupiter', 10: 'Sun', 11: 'Jupiter', 12: 'Saturn'
            };

            const HOUSE_THEMES: Record<number, { titleEn: string; titleHi: string; domainEn: string; domainHi: string }> = {
              1: { titleEn: 'Self, Vitality & Core Identity', titleHi: 'लग्न, व्यक्तित्व एवं ऊर्जा', domainEn: 'Personal initiative, physical vitality, and self-projection', domainHi: 'व्यक्तिगत पहल, शारीरिक ऊर्जा और आत्म-प्रकटन' },
              2: { titleEn: 'Wealth, Speech & Family Values', titleHi: 'धन, वाणी एवं कुटुंब', domainEn: 'Financial resources, speech cadence, and accumulated assets', domainHi: 'वित्तीय संसाधन, वाणी और संचित संपत्ति' },
              3: { titleEn: 'Courage, Skills & Siblings', titleHi: 'पराक्रम, कौशल एवं सहोदर', domainEn: 'Short journeys, personal courage, initiatives, and skills', domainHi: 'छोटी यात्राएं, साहस, पहल और कौशल' },
              4: { titleEn: 'Home, Subconscious & Emotional Roots', titleHi: 'सुख, मानसिक शांति एवं अवचेतन', domainEn: 'Inner peace, emotional sensitivity, home environment, and subconscious patterns', domainHi: 'मानसिक शांति, भावनात्मक संवेदनशीलता, घरेलू माहौल और अवचेतन पैटर्न' },
              5: { titleEn: 'Intellect, Creativity & Purva Punya', titleHi: 'बुद्धि, संतान एवं पूर्व पुण्य', domainEn: 'Higher intelligence, creative expression, speculation, and merit', domainHi: 'उच्च बुद्धि, रचनात्मक अभिव्यक्ति, संतान और पूर्व पुण्य' },
              6: { titleEn: 'Service, Health & Obstacles', titleHi: 'रोग, ऋण एवं शत्रु (सेवा)', domainEn: 'Daily routines, health management, competition, and overcoming obstacles', domainHi: 'दैनिक कार्य, स्वास्थ्य प्रबंधन, प्रतिस्पर्धा और बाधाओं पर विजय' },
              7: { titleEn: 'Partnerships, Marriage & Public', titleHi: 'विवाह, साझेदारी एवं जनसंपर्क', domainEn: 'Spouse, business partnerships, public interactions, and open contracts', domainHi: 'जीवनसाथी, व्यावसायिक साझेदारी, जनसंपर्क और अनुबंध' },
              8: { titleEn: 'Transformation, Longevity & Hidden', titleHi: 'आयु, गुप्त रहस्य एवं रूपांतरण', domainEn: 'Deep psychological transformation, occult, sudden gains/losses, and longevity', domainHi: 'गहन मनोवैज्ञानिक परिवर्तन, गुप्त विद्या, आकस्मिक लाभ/हानि और आयु' },
              9: { titleEn: 'Higher Wisdom, Fortune & Dharma', titleHi: 'भाग्य, उच्च ज्ञान एवं धर्म', domainEn: 'Higher philosophy, long-distance travel, spiritual fortune, and mentors', domainHi: 'उच्च दर्शन, लंबी यात्राएं, आध्यात्मिक भाग्य और गुरु' },
              10: { titleEn: 'Career, Status & Public Action', titleHi: 'कर्म, करियर एवं सामाजिक प्रतिष्ठा', domainEn: 'Professional aspirations, career milestones, authority, and public recognition', domainHi: 'व्यावसायिक आकांक्षाएं, करियर के मील के पत्थर, अधिकार और प्रतिष्ठा' },
              11: { titleEn: 'Gains, Networks & Aspirations', titleHi: 'लाभ, सामाजिक नेटवर्क एवं इच्छाएं', domainEn: 'Financial gains, friendship circles, fulfillment of desires, and net networks', domainHi: 'आर्थिक लाभ, मित्र मंडली, इच्छाओं की पूर्ति और नेटवर्क' },
              12: { titleEn: 'Liberation, Spirituality & Expenses', titleHi: 'व्यय, मोक्ष एवं आध्यात्मिक मुक्ति', domainEn: 'Spiritual liberation, subconscious release, foreign lands, and isolation', domainHi: 'आध्यात्मिक मुक्ति, अवचेतन विमोचन, विदेश यात्रा और एकांत' },
            };

            const PLANET_FLAVORS: Record<string, { flavorEn: string; flavorHi: string }> = {
              Sun: { flavorEn: 'Brings authority, visibility, and focused ego illumination to this domain.', flavorHi: 'इस क्षेत्र में अधिकार, दृश्यता और केंद्रित ऊर्जा लाता है।' },
              Moon: { flavorEn: 'Infuses emotional depth, fluctuation, subconscious sensitivity, and receptivity.', flavorHi: 'भावनात्मक गहराई, संवेदनशीलता और अवचेतन तरंगें उत्पन्न करता है।' },
              Mars: { flavorEn: 'Triggers action, drive, competitive push, and decisive energy.', flavorHi: 'कार्यवाही, गतिशीलता, प्रतिस्पर्धी जोर और निर्णायक ऊर्जा को सक्रिय करता है।' },
              Mercury: { flavorEn: 'Brings analytical thinking, communication, coordination, and learning.', flavorHi: 'विश्लेषणात्मक सोच, संवाद, समन्वय और सीखने की प्रवृत्ति को बढ़ाता है।' },
              Jupiter: { flavorEn: 'Expands wisdom, optimism, protective grace, and growth opportunities.', flavorHi: 'ज्ञान, आशावाद, सुरक्षात्मक कृपा और विकास के अवसर प्रदान करता है।' },
              Venus: { flavorEn: 'Brings comfort, relational harmony, aesthetic pleasure, and desires.', flavorHi: 'सुख-सुविधा, संबंधीय सामंजस्य, सौंदर्य और आनंद की इच्छाओं को जगाता है।' },
              Saturn: { flavorEn: 'Brings karmic discipline, structural tests, delays, and enduring responsibility.', flavorHi: 'कर्मिक अनुशासन, संरचनात्मक परीक्षा, विलंब और दीर्घकालिक जिम्मेदारी लाता है।' },
              Rahu: { flavorEn: 'Amplifies ambition, unconventional drives, obsessions, and sudden shifts.', flavorHi: 'महकांक्षा, अपरंपरागत प्रवृत्तियों, मोह और अचानक बदलावों को तीव्र करता है।' },
              Ketu: { flavorEn: 'Triggers spiritual detachment, sudden intuition, subtle insights, and letting go.', flavorHi: 'आध्यात्मिक वैराग्य, अंतर्ज्ञान, सूक्ष्म अंतर्दृष्टि और त्याग की भावना जगाता है।' }
            };

            const currentTheme = HOUSE_THEMES[houseNumber] || HOUSE_THEMES[1];
            const activePDPlanet = currentPD?.planet;
            const activeFlavor = activePDPlanet ? PLANET_FLAVORS[activePDPlanet] : null;

            const evalPlanet = (planetName: string) => {
              if (!planetName) return null;
              const isLord = signLord === planetName;
              const isOccupant = occupants.includes(planetName);
              const isAspecting = aspectingPlanets.includes(planetName);
              const isKaraka = NATURAL_KARAKAS[houseNumber] === planetName;
              const reasons: string[] = [];
              if (isLord) reasons.push(isHi ? `भावेश (Lord of H${houseNumber})` : `Sign Lord of House ${houseNumber}`);
              if (isOccupant) reasons.push(isHi ? `भाव स्थित (Occupant)` : `Seated in House ${houseNumber}`);
              if (isAspecting) reasons.push(isHi ? `दृष्टिकोण सक्रियक (Aspecting)` : `Casts Drishti on House ${houseNumber}`);
              if (isKaraka) reasons.push(isHi ? `नैसर्गिक कारक (Karaka)` : `Natural Karaka for House ${houseNumber}`);
              return { planetName, isLord, isOccupant, isAspecting, isKaraka, isActive: reasons.length > 0, reasons };
            };

            const mdEval = evalPlanet(currentMD?.planet);
            const adEval = evalPlanet(currentAD?.planet);
            const pdEval = evalPlanet(currentPD?.planet);

            return (
              <div className="p-4 bg-gradient-to-br from-amber-50/90 to-amber-100/50 rounded-xl border border-amber-300/90 space-y-3 text-xs shadow-xs">
                <div className="flex items-center justify-between border-b border-amber-200 pb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-700" />
                    <div>
                      <h4 className="font-bold text-amber-950 uppercase tracking-wider text-[11px] font-serif">
                        {isHi ? 'विंशोत्तरी दशा, अंतरदशा एवं प्रत्यंतर दशा भाव सक्रियक विश्लेषण' : 'Pratyantar & Dasha House Activation Engine'}
                      </h4>
                      <span className="text-[10px] text-amber-800 font-medium">
                        Domain: {isHi ? currentTheme.titleHi : currentTheme.titleEn}
                      </span>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-200 text-amber-900 border border-amber-300 font-mono">
                    House {houseNumber} Trigger
                  </span>
                </div>

                <p className="text-[11px] text-amber-950 leading-relaxed">
                  {isHi ? (
                    <span>
                      <strong className="font-semibold">भाव क्षेत्र एवं सक्रियक नियम:</strong> यह भाव मुख्य रूप से <em className="underline">{currentTheme.domainHi}</em> से संबंधित है। जब महादशा (MD), अंतर्दशा (AD) और प्रत्यंतर (PD) स्वामी इस भाव से जुड़ते हैं, तो इसके परिणाम प्रकट होते हैं।
                    </span>
                  ) : (
                    <span>
                      <strong className="font-semibold">Bhava Domain & Timing:</strong> This house governs <em className="underline">{currentTheme.domainEn}</em>. When Mahadasha (MD), Antardasha (AD), and Pratyantar (PD) lords connect through lordship, occupancy, or aspect, this life area becomes actively triggered.
                    </span>
                  )}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                  {/* Mahadasha */}
                  <div className={`p-3 rounded-lg border ${mdEval?.isActive ? 'bg-white border-emerald-300 shadow-2xs' : 'bg-white/60 border-amber-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase text-astro-secondary-text">Mahadasha (MD)</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${mdEval?.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-astro-card text-astro-secondary-text'}`}>
                        {mdEval?.isActive ? (isHi ? 'सक्रिय (Active)' : 'Activating') : (isHi ? 'तटस्थ (Neutral)' : 'Background')}
                      </span>
                    </div>
                    <div className="font-bold text-astro-primary text-sm">
                      {currentMD?.planet ? getPlanetFullName(currentMD.planet, activeLang) : 'None'}
                    </div>
                    <div className="text-[10px] text-astro-text mt-1 space-y-0.5">
                      {mdEval?.reasons.length ? mdEval.reasons.map((r, idx) => <div key={idx} className="text-emerald-900 font-medium">• {r}</div>) : <div className="text-astro-secondary-text italic">Indirect macro support</div>}
                    </div>
                  </div>

                  {/* Antardasha */}
                  <div className={`p-3 rounded-lg border ${adEval?.isActive ? 'bg-white border-emerald-300 shadow-2xs' : 'bg-white/60 border-amber-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase text-astro-secondary-text">Antardasha (AD)</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${adEval?.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-astro-card text-astro-secondary-text'}`}>
                        {adEval?.isActive ? (isHi ? 'सक्रिय (Active)' : 'Activating') : (isHi ? 'तटस्थ (Neutral)' : 'Background')}
                      </span>
                    </div>
                    <div className="font-bold text-astro-primary text-sm">
                      {currentAD?.planet ? getPlanetFullName(currentAD.planet, activeLang) : 'None'}
                    </div>
                    <div className="text-[10px] text-astro-text mt-1 space-y-0.5">
                      {adEval?.reasons.length ? adEval.reasons.map((r, idx) => <div key={idx} className="text-emerald-900 font-medium">• {r}</div>) : <div className="text-astro-secondary-text italic">Secondary structural conduit</div>}
                    </div>
                  </div>

                  {/* Pratyantar Dasha */}
                  <div className={`p-3 rounded-lg border ${pdEval?.isActive ? 'bg-white border-amber-400 shadow-2xs' : 'bg-white/60 border-amber-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase text-amber-900 font-bold">Pratyantar (PD - Trigger)</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${pdEval?.isActive ? 'bg-amber-500 text-white' : 'bg-astro-card text-astro-secondary-text'}`}>
                        {pdEval?.isActive ? (isHi ? 'तात्कालिक उत्प्रेरक' : 'Direct Trigger') : (isHi ? 'सामान्य' : 'Supporting')}
                      </span>
                    </div>
                    <div className="font-bold text-amber-950 text-sm">
                      {currentPD?.planet ? getPlanetFullName(currentPD.planet, activeLang) : 'None'}
                    </div>
                    <div className="text-[10px] text-astro-text mt-1 space-y-0.5">
                      {pdEval?.reasons.length ? pdEval.reasons.map((r, idx) => <div key={idx} className="text-amber-950 font-bold">• {r}</div>) : <div className="text-astro-secondary-text italic">General micro-flow</div>}
                    </div>
                  </div>
                </div>

                {/* Planetary Experiential Flavor Modifier */}
                {activeFlavor && (
                  <div className="p-2.5 bg-amber-50/90 rounded-lg border border-amber-300 text-[11px] text-amber-950 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-semibold">{isHi ? `प्रत्यंतर स्वामी (${currentPD?.planet}) का अनुभवजन्य प्रभाव: ` : `Pratyantar Lord (${currentPD?.planet}) Experiential Flavor: `}</strong>
                      {isHi ? activeFlavor.flavorHi : activeFlavor.flavorEn}
                    </div>
                  </div>
                )}

                <div className="p-2.5 bg-white rounded-lg border border-amber-200 text-[11px] text-amber-950 leading-relaxed flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-semibold">{isHi ? 'तार्किक निष्कर्ष: ' : 'Logical Conclusion: '}</strong>
                    {pdEval?.isActive 
                      ? (isHi ? `वर्तमान प्रत्यंतर स्वामी (${currentPD?.planet}) House ${houseNumber} (${currentTheme.titleHi}) को सक्रिय कर रहा है। ${activeFlavor?.flavorHi || ''}` : `The running Pratyantar lord (${currentPD?.planet}) directly activates House ${houseNumber} (${currentTheme.titleEn}). ${activeFlavor?.flavorEn || ''}`)
                      : (isHi ? `वर्तमान प्रत्यंतर स्वामी House ${houseNumber} के मामलों को परोक्ष रूप से प्रभावित कर रहा है; मुख्य प्रकटन महादशा और अंतर्दशा के माध्यम से हो रहा है।` : `The active Pratyantar lord connects indirectly to House ${houseNumber} (${currentTheme.titleEn}); primary fruition is governed by the operating Mahadasha and Antardasha.`)}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Navigation Sub-Tabs (Unified 4-Step Analytical Architecture) */}
          <div className="flex flex-wrap items-center gap-1.5 border-b border-astro-border pb-2">
            <button
              onClick={() => setActiveTab('HUMANIZED')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeTab === 'HUMANIZED'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5 text-astro-accent" />
              {isHi ? '1. जीवंत फलादेश (Conversational Summary)' : '1. Conversational Summary'}
            </button>
            <button
              onClick={() => setActiveTab('TRIPOD')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeTab === 'TRIPOD'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card'
              }`}
            >
              <Layers className="w-3.5 h-3.5 text-astro-accent" />
              {isHi ? '2. त्रि-स्तंभ व ग्रहीय स्थिति (Tripod & Dignity Matrix)' : '2. Tripod & Dignity Matrix'}
            </button>
            <button
              onClick={() => setActiveTab('ADVANCED')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeTab === 'ADVANCED'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card'
              }`}
            >
              <Scale className="w-3.5 h-3.5 text-astro-accent" />
              {isHi ? '3. भावत भावम् व नवांश (Advanced Parashari)' : '3. Advanced Parashari'}
            </button>
            <button
              onClick={() => setActiveTab('SYNTHESIS')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                activeTab === 'SYNTHESIS'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card'
              }`}
            >
              <Zap className="w-3.5 h-3.5 text-astro-accent" />
              {isHi ? '4. समय चक्र व कर्म सुधार (Timing & Karmic Action Plan)' : '4. Timing & Karmic Action Plan'}
            </button>
          </div>

          {/* TAB 0: HUMANIZED INDIAN CONVERSATIONAL VOICE */}
          {activeTab === 'HUMANIZED' && (
            <div className="space-y-3 pt-1 text-xs">
              <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3.5">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border pb-2.5">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-astro-accent" />
                    <span className="font-serif font-bold text-astro-primary text-sm">
                      {archetype.titleHinglish}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase font-bold text-astro-accent bg-astro-accent/10 px-2 py-0.5 rounded-full border border-astro-accent/20">
                      Tone: {archetype.toneDescriptionEn.split(',')[0]}
                    </span>
                    <span className="text-[10px] font-bold bg-astro-primary text-white px-2 py-0.5 rounded-full">
                      Keyword: {humanizedReading.tonePill}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-astro-secondary-text block">
                    ⚡ Authentic Conversational Interpretation (Hinglish):
                  </span>
                  <p className="text-xs sm:text-sm text-astro-text leading-relaxed bg-astro-bg p-3.5 rounded-xl border border-astro-border font-sans">
                    {humanizedReading.executiveHinglish}
                  </p>
                </div>

                <div className="bg-astro-primary/5 border border-astro-primary/20 p-3 rounded-xl flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-astro-primary block text-[11px]">
                      Practical Action Step (Thos Faisla):
                    </span>
                    <p className="text-xs text-[#384342] mt-0.5">
                      {humanizedReading.actionAdviceHinglish}
                    </p>
                  </div>
                </div>

                {/* Archetype Keywords Pill List */}
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-[10px] font-semibold text-astro-secondary-text mr-1">Archetype Vocabulary:</span>
                  {archetype.signatureKeywords.map((kw, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-md bg-astro-bg border border-astro-border text-astro-primary font-medium">
                      #{kw}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 1: THE TRIPOD RULE (Vessel, Manager, Karaka) - Merged Comprehensive Astrological Synthesis */}
          {activeTab === 'TRIPOD' && (
            <div className="space-y-4 pt-1 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 text-xs">
              {/* 1. Bhava (The Vessel) */}
              <div className="bg-astro-card/70 border border-astro-border rounded-xl p-3.5 space-y-2.5 flex flex-col justify-between shadow-2xs">
                <div className="space-y-2">
                  <div className="flex items-center justify-between border-b border-astro-border pb-2">
                    <div className="flex items-center gap-1.5">
                      <CircleDot className="w-4 h-4 text-astro-primary" />
                      <span className="font-bold text-astro-primary uppercase tracking-wider text-[11px] font-serif">
                        {isHi ? '१. भाव (आधार स्तंभ / Vessel)' : '1. Bhava (The Vessel)'}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono font-bold text-astro-primary bg-white px-2 py-0.5 rounded border border-astro-border">
                      {triPillar.vessel.netVesselScore}/100
                    </span>
                  </div>

                  <div className="space-y-2 text-[11px]">
                    {/* Sign, Nature & Elements */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-astro-secondary-text">{isHi ? 'राशि एवं तत्व:' : 'Sign & Tattva:'}</span>
                        <strong className="text-astro-text">{triPillar.vessel.sign} ({triPillar.vessel.signSanskrit})</strong>
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-astro-secondary-text">{isHi ? 'तत्व व स्वभाव:' : 'Element & Modality:'}</span>
                        <span className="font-semibold text-astro-primary">
                          {isHi ? `${signTrait.elementHi} • ${signTrait.modalityHi}` : `${signTrait.elementEn} • ${signTrait.modalityEn}`}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-astro-secondary-text">{isHi ? 'वर्गीकरण व उद्देश्य:' : 'Category & Aim:'}</span>
                        <span className="font-medium text-astro-text">
                          {isHi ? `${houseClass.categoryHi} • ${houseClass.aimHi}` : `${triPillar.vessel.houseCategory} • ${houseClass.aimEn}`}
                        </span>
                      </div>
                    </div>

                    {/* Ashtakavarga SAV & D-Chart Focus */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border flex items-center justify-between">
                      <div>
                        <span className="text-[10px] text-astro-secondary-text block font-semibold uppercase">{isHi ? 'अष्टकवर्ग (SAV):' : 'SAV Vitality:'}</span>
                        <span className="font-mono font-bold text-astro-primary text-xs">{savPoints} / 56 {isHi ? 'बिंदु' : 'Pts'}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] text-astro-secondary-text block font-semibold uppercase">{isHi ? 'संबंधित वर्ग कुंडली:' : 'D-Chart Focus:'}</span>
                        <span className="text-[10px] font-bold text-astro-accent">{houseClass.dChart}</span>
                      </div>
                    </div>

                    {/* Occupants with Baladi Avastha, BAV, Shadbala & Combustion */}
                    <div className="pt-0.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase font-bold text-astro-secondary-text">
                          {isHi ? `भाव स्थित ग्रह (${occupants.length}):` : `Occupants (${occupants.length}):`}
                        </span>
                        {occupants.length > 1 && (
                          <span className="text-[9px] px-1.5 py-0.2 rounded bg-astro-primary/10 text-astro-primary font-semibold">
                            {conjunctionDynamics ? (isHi ? conjunctionDynamics[0]?.chemistryHi : conjunctionDynamics[0]?.chemistry) : ''}
                          </span>
                        )}
                      </div>

                      {occupants.length > 0 ? (
                        <div className="space-y-1.5">
                          {occupants.map((occ) => {
                            const isOdd = [0, 2, 4, 6, 8, 10].includes(occ.signIndex ?? 0);
                            const avastha = getBaladiAvastha(occ.degreeInSign ?? 15, isOdd);
                            const occEnh = enhancedDignityMap?.[occ.name];
                            const houseObj = kundali.houses?.find(h => h.houseNumber === houseNumber);
                            const signIdx = houseObj ? houseObj.signIndex : 0;
                            const bavScore = prastaraTables[occ.name]?.signTotals?.[signIdx];
                            const occShad = shadbalaData?.planets?.find((s: any) => s.planet === occ.name);

                            return (
                              <div key={occ.name} className="p-1.5 bg-white rounded-lg border border-astro-border space-y-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5">
                                    <span className="w-5 h-5 rounded bg-astro-primary text-white font-bold flex items-center justify-center text-[9px]">
                                      {getPlanetShortCode(occ.name, activeLang)}
                                    </span>
                                    <span className="font-bold text-astro-text">{getPlanetFullName(occ.name as any, activeLang)}</span>
                                    <span className="text-[10px] font-mono text-astro-secondary-text">
                                      {Math.floor(occ.degreeInSign ?? 0)}°{Math.floor(((occ.degreeInSign ?? 0) % 1) * 60)}'
                                    </span>
                                  </div>
                                  <span className={`text-[9px] px-1.5 py-0.2 rounded border ${getDignityBadgeClass(occEnh?.dignity || occ.dignity || 'Neutral')}`}>
                                    {occEnh?.dignityLabelEn || occ.dignity}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between text-[9.5px] pt-0.5 border-t border-astro-border/40">
                                  <div className="flex items-center gap-1 text-astro-secondary-text">
                                    <span>{isHi ? avastha.nameHi : avastha.nameEn}</span>
                                    {occ.isCombust && <span className="text-rose-600 font-bold">{isHi ? '• अस्त' : '• Combust'}</span>}
                                    {occ.isRetrograde && <span className="text-amber-600 font-bold">{isHi ? '• वक्री' : '• Vakri'}</span>}
                                  </div>

                                  {/* BAV & Shadbala Badge with Hover */}
                                  <PopoverWrapper
                                    enabled={tableTooltipsEnabled}
                                    trigger={
                                      <div className="flex items-center gap-1 cursor-help">
                                        {(occShad?.ratio !== undefined || occShad?.shadbalaRatio !== undefined) && (
                                          <span className="text-[9px] font-mono font-semibold text-astro-primary bg-astro-primary/5 px-1 rounded border border-astro-primary/20">
                                            {((occShad.ratio ?? occShad.shadbalaRatio) as number).toFixed(2)}x
                                          </span>
                                        )}
                                        {bavScore !== undefined && (
                                          <span className={`text-[9px] font-mono font-bold px-1.5 py-0.2 rounded border ${
                                            bavScore >= 5 ? 'bg-emerald-100 text-emerald-900 border-emerald-300' :
                                            bavScore === 4 ? 'bg-teal-100 text-teal-900 border-teal-300' :
                                            'bg-rose-100 text-rose-900 border-rose-300'
                                          }`}>
                                            {bavScore}/8 BAV
                                          </span>
                                        )}
                                      </div>
                                    }
                                    content={
                                      <BavHoverCard
                                        planetName={occ.name}
                                        signName={triPillar.vessel.sign}
                                        houseNumber={houseNumber}
                                        bavScore={bavScore}
                                        contributorsMap={prastaraTables[occ.name]?.contributors?.[signIdx]}
                                        lang={activeLang}
                                      />
                                    }
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="p-2 bg-white/70 rounded-lg border border-astro-border text-astro-secondary-text italic text-[10px] text-center">
                          {isHi ? 'रिक्त भाव (दृष्टि एवं भावेश द्वारा संचालित)' : 'Empty / Unoccupied (Governed via Drishti & Lord)'}
                        </div>
                      )}
                    </div>

                    {/* Inbound Aspects */}
                    <div className="pt-0.5">
                      <span className="text-[10px] uppercase font-bold text-astro-secondary-text block mb-1">
                        {isHi ? `आवक दृष्टियाँ (${triPillar.vessel.inboundDrishti.length}):` : `Inbound Aspects (${triPillar.vessel.inboundDrishti.length}):`}
                      </span>
                      {triPillar.vessel.inboundDrishti.length > 0 ? (
                        <div className="space-y-1 max-h-28 overflow-y-auto pr-0.5">
                          {triPillar.vessel.inboundDrishti.map((asp, idx) => (
                            <PopoverWrapper
                              key={idx}
                              enabled={tableTooltipsEnabled}
                              trigger={
                                <div className="p-1.5 bg-white/90 hover:bg-white rounded border border-astro-border text-[10px] flex items-center justify-between cursor-help transition-colors">
                                  <div className="flex items-center gap-1.5 font-medium text-astro-text">
                                    <span className="w-4 h-4 rounded bg-astro-primary/10 text-astro-primary font-bold flex items-center justify-center text-[8px]">
                                      {getPlanetShortCode(asp.planet, activeLang)}
                                    </span>
                                    <span>{getPlanetFullName(asp.planet as any, activeLang)}</span>
                                    <span className="text-[9px] text-astro-secondary-text">(H{asp.fromHouse})</span>
                                  </div>
                                  <span className={`px-1.5 py-0.2 rounded text-[8.5px] font-mono font-bold border ${
                                    asp.isBenefic 
                                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300' 
                                      : 'bg-rose-50 text-rose-800 border-rose-300'
                                  }`}>
                                    {asp.drishtiStrengthVirupas}/60 Virupas
                                  </span>
                                </div>
                              }
                              content={
                                <DrishtiAspectHoverCard
                                  planetName={asp.planet}
                                  aspectType={(asp as any).aspectType || 'Full Aspect'}
                                  fromHouse={asp.fromHouse}
                                  toHouse={houseNumber}
                                  drishtiStrengthVirupas={asp.drishtiStrengthVirupas}
                                  isBenefic={asp.isBenefic}
                                  lang={activeLang}
                                />
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-astro-secondary-text italic text-[10px] block">
                          {isHi ? 'कोई प्रत्यक्ष पराशरी दृष्टि नहीं' : 'No direct classical aspects'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Kartari Status Footer */}
                <div className="pt-2 border-t border-astro-border text-[10px] flex items-center justify-between">
                  <span className="text-astro-secondary-text font-semibold uppercase">{isHi ? 'कर्तरी स्थिति:' : 'Kartari Status:'}</span>
                  <span className={`font-semibold px-2 py-0.5 rounded text-[9px] ${
                    triPillar.vessel.kartariStatus.includes('Shubha') 
                      ? 'bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold' 
                      : triPillar.vessel.kartariStatus.includes('Papa') 
                      ? 'bg-rose-100 text-rose-900 border border-rose-300 font-bold' 
                      : 'bg-white text-astro-text border border-astro-border'
                  }`}>
                    {triPillar.vessel.kartariStatus}
                  </span>
                </div>
              </div>

              {/* 2. Bhavesha (The Resource Manager / Lord) */}
              <div className="bg-astro-card/70 border border-astro-border rounded-xl p-3.5 space-y-2.5 flex flex-col justify-between shadow-2xs">
                <div className="space-y-2">
                  <div className="flex items-center justify-between border-b border-astro-border pb-2">
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-4 h-4 text-astro-primary" />
                      <span className="font-bold text-astro-primary uppercase tracking-wider text-[11px] font-serif">
                        {isHi ? '२. भावेश (कार्यकारी स्वामी / Lord)' : '2. Bhavesha (The Lord)'}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono font-bold text-astro-primary bg-white px-2 py-0.5 rounded border border-astro-border">
                      {triPillar.manager.netLordScore}/100
                    </span>
                  </div>

                  <div className="space-y-2 text-[11px]">
                    {/* Lord Identity & Placement */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-astro-secondary-text">{isHi ? 'राशि स्वामी (भावेश):' : 'Sign Lord:'}</span>
                        <div className="flex items-center gap-1">
                          <strong className="text-astro-primary text-xs">{getPlanetFullName(houseLordName as any, activeLang)}</strong>
                          {lordChara && (
                            <span className="text-[9px] px-1 py-0.2 rounded bg-amber-100 text-amber-900 border border-amber-300 font-bold font-mono">
                              {lordChara.key}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-astro-secondary-text">{isHi ? 'स्थिति (घर व राशि):' : 'Placement:'}</span>
                        <span className="font-semibold text-astro-text">
                          House {triPillar.manager.placedInHouse} in {triPillar.manager.placedInSign}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-astro-secondary-text">{isHi ? 'स्वराशि से विस्थापन:' : 'Displacement:'}</span>
                        <span className="font-medium text-astro-primary">
                          {formatOrdinal(triPillar.manager.houseFromOwnSign)} from own sign
                        </span>
                      </div>
                    </div>

                    {/* Dignity & Shadbala Strength */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-astro-secondary-text uppercase">{isHi ? 'गरिमा (९-स्तरीय):' : '9-Tier Dignity:'}</span>
                        <span className="text-[10px] font-mono font-bold text-astro-primary">
                          {triPillar.manager.dignityScorePoints}/60 Pts
                        </span>
                      </div>
                      <span className={`text-[10px] block px-2 py-0.5 rounded border text-center font-semibold ${getDignityBadgeClass(lordEnhanced?.dignity || triPillar.manager.saptaVargaDignity)}`}>
                        {lordEnhanced?.dignityLabelEn || triPillar.manager.saptaVargaDignity}
                      </span>
                      <div className="flex items-center justify-between text-[10px] pt-0.5 border-t border-astro-border/60">
                        <span className="text-astro-secondary-text">{isHi ? 'षड्बल बल अनुपात:' : 'Shadbala Strength:'}</span>
                        <span className="font-mono font-bold text-astro-primary">
                          {(lordShadbala?.ratio ?? lordShadbala?.shadbalaRatio) ? `${((lordShadbala.ratio ?? lordShadbala.shadbalaRatio) as number).toFixed(2)}x Ratio` : '1.00x'} ({((lordShadbala?.totalVirupas ?? lordShadbala?.totalShadbalaVirupas) as number)?.toFixed(0) || '390'} Virupas)
                        </span>
                      </div>
                    </div>

                    {/* Dispositor Host & Panchadha Maitri */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="font-bold text-astro-secondary-text uppercase">{isHi ? 'डिस्पोज़िटर एवं पञ्चधा मैत्री:' : 'Dispositor Dynamics:'}</span>
                        <span className={`px-1.5 py-0.2 rounded font-bold text-[9px] ${
                          lordDispositorRel.compound.includes('Friend') || lordDispositorRel.compound.includes('Own')
                            ? 'bg-emerald-100 text-emerald-800'
                            : lordDispositorRel.compound.includes('Enemy')
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-astro-secondary/15 text-astro-secondary'
                        }`}>
                          {isHi ? lordDispositorRel.compoundHi : lordDispositorRel.compoundEn}
                        </span>
                      </div>
                      <p className="text-[10px] text-astro-text">
                        Hosted by <strong className="text-astro-primary">{lordDispositorName || triPillar.manager.dispositor.planet}</strong> in H{triPillar.manager.dispositor.house}
                      </p>
                    </div>

                    {/* Baladi Avastha & Functional Nature */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1 text-[10px]">
                      <div className="flex justify-between items-center">
                        <span className="text-astro-secondary-text">{isHi ? 'बाल्यादि अवस्था:' : 'Baladi Avastha:'}</span>
                        <span className="font-semibold text-astro-text">
                          {isHi ? lordAvastha.nameHi : lordAvastha.nameEn} ({lordAvastha.potencyPct}% Potency)
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-astro-secondary-text">{isHi ? 'कार्यात्मक प्रकृति (लग्न अनुसार):' : 'Functional Nature:'}</span>
                        <span className="font-medium text-astro-primary">{lordFunctional}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Combustion & Motion Footer */}
                <div className="pt-2 border-t border-astro-border text-[10px] flex items-center justify-between">
                  <span className="text-astro-secondary-text">{isHi ? 'अस्त / गति:' : 'Combustion / Motion:'}</span>
                  <span className="font-semibold text-astro-text">
                    {triPillar.manager.combustion.isCombust ? (isHi ? 'अस्त (Combust)' : 'Combust (Asta)') : (isHi ? 'उदित (Uncombust)' : 'Uncombust')} • {triPillar.manager.retrogradation.isVakri ? (isHi ? 'वक्री (R)' : 'Vakri (R)') : (isHi ? 'मार्गी (Direct)' : 'Direct')}
                  </span>
                </div>
              </div>

              {/* 3. Karaka (The Deliverable / Cosmic Signifier) */}
              <div className="bg-astro-card/70 border border-astro-border rounded-xl p-3.5 space-y-2.5 flex flex-col justify-between shadow-2xs">
                <div className="space-y-2">
                  <div className="flex items-center justify-between border-b border-astro-border pb-2">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4 text-astro-accent" />
                      <span className="font-bold text-astro-accent uppercase tracking-wider text-[11px] font-serif">
                        {isHi ? '३. कारक (दिव्य संकेतक / Deliverable)' : '3. Karaka (Deliverable)'}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono font-bold text-astro-primary bg-white px-2 py-0.5 rounded border border-astro-border">
                      {triPillar.deliverable.karakaVitalityScore}/100
                    </span>
                  </div>

                  <div className="space-y-2 text-[11px]">
                    {/* Primary Fixed Karaka Placement */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-astro-secondary-text">{isHi ? 'स्थिर कारक (मुख्य):' : 'Primary Fixed Karaka:'}</span>
                        <div className="flex items-center gap-1">
                          <strong className="text-astro-accent text-xs">{getPlanetFullName(primaryKarakaName as any, activeLang)}</strong>
                          {karakaChara && (
                            <span className="text-[9px] px-1 py-0.2 rounded bg-amber-100 text-amber-900 border border-amber-300 font-bold font-mono">
                              {karakaChara.key}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-astro-secondary-text">{isHi ? 'स्थिति (घर व राशि):' : 'Placement:'}</span>
                        <span className="font-semibold text-astro-text">
                          House {triPillar.deliverable.placedInHouse} in {triPillar.deliverable.placedInSign}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-[10px]">
                        <span className="text-astro-secondary-text">{isHi ? 'सर्व स्थिर कारक:' : 'All Fixed Signifiers:'}</span>
                        <span className="font-medium text-astro-text">{karakaInfo.fixed.join(', ')}</span>
                      </div>
                    </div>

                    {/* Karaka Dignity & Shadbala */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-astro-secondary-text uppercase">{isHi ? 'कारक गरिमा:' : 'Karaka Dignity:'}</span>
                        <span className="text-[10px] font-mono font-bold text-astro-primary">
                          {triPillar.deliverable.karakaVitalityScore}/100 Pts
                        </span>
                      </div>
                      <span className={`text-[10px] block px-2 py-0.5 rounded border text-center font-semibold ${getDignityBadgeClass(triPillar.deliverable.karakaDignity)}`}>
                        {triPillar.deliverable.karakaDignity}
                      </span>
                      <div className="flex items-center justify-between text-[10px] pt-0.5 border-t border-astro-border/60">
                        <span className="text-astro-secondary-text">{isHi ? 'षड्बल बल:' : 'Shadbala Strength:'}</span>
                        <span className="font-mono font-bold text-astro-accent">
                          {(karakaShadbala?.ratio ?? karakaShadbala?.shadbalaRatio) ? `${((karakaShadbala.ratio ?? karakaShadbala?.shadbalaRatio) as number).toFixed(2)}x Ratio` : '1.05x Ratio'} ({((karakaShadbala?.totalVirupas ?? karakaShadbala?.totalShadbalaVirupas) as number)?.toFixed(0) || '380'} Virupas)
                        </span>
                      </div>
                    </div>

                    {/* Karaka Significance Note */}
                    <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1 text-[10px]">
                      <span className="font-bold text-astro-secondary-text uppercase block">{isHi ? 'कारकत्व क्षेत्र:' : 'Signification Scope:'}</span>
                      <p className="text-astro-text leading-tight">
                        {isHi ? karakaInfo.noteHi : karakaInfo.noteEn}
                      </p>
                    </div>

                    {/* Karako Bhava Nashaya Check */}
                    {triPillar.deliverable.karakoBhavanashaya ? (
                      <div className="p-2 bg-amber-50 rounded-lg border border-amber-300 text-amber-950 space-y-1">
                        <div className="flex items-center gap-1 font-bold text-[10px] uppercase text-amber-900">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-700" /> {isHi ? 'कारको भाव नाशाय सतर्कता' : 'Karako Bhavo Nashaya Precaution'}
                        </div>
                        <p className="text-[10px] leading-tight">
                          {triPillar.deliverable.karakoBhavanashayaNote}
                        </p>
                      </div>
                    ) : (
                      <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200 text-emerald-900 text-[10px] flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                        <span>{isHi ? 'कारको भाव नाशाय दोष से मुक्त (संतुलित स्थिति)।' : 'Karako Bhavo Nashaya clear (Well displaced or supported).'}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Karaka Manifestation Note Footer */}
                <div className="pt-2 border-t border-astro-border text-[10px] text-astro-secondary-text">
                  <span>{isHi ? 'कारक भाव के प्राकृतिक फलों के प्रकटीकरण का दिव्य आधार है।' : 'Guarantees the organic manifestation of core house fruits.'}</span>
                </div>
              </div>
            </div>

            {/* Embedded Planetary Dignity & Relationship Table */}
            {(() => {
              const allPlanets = [...chartData.planets];
              const baseRelevantPlanets = allPlanets.filter((p) => {
                if (p.name === 'Ascendant') return false;
                const isOccupant = p.house === houseNumber;
                const isLord = p.name === houseLordName;
                const isAspecting = (triPillar.vessel.inboundDrishti || []).some(a => a.planet === p.name);
                return isOccupant || isLord || isAspecting;
              }).sort((a, b) => {
                const getSequenceTier = (pl: typeof a) => {
                  const isOccupant = pl.house === houseNumber;
                  const isLord = pl.name === houseLordName;
                  if (isOccupant) return 1;
                  if (isLord) return 2;
                  return 3;
                };
                const tierA = getSequenceTier(a);
                const tierB = getSequenceTier(b);
                if (tierA !== tierB) return tierA - tierB;
                return a.house - b.house;
              });

              // Filter based on selected tab filter
              const relevantPlanets = baseRelevantPlanets.filter((p) => {
                if (matrixFilter === 'OCCUPANT') return p.house === houseNumber;
                if (matrixFilter === 'LORD') return p.name === houseLordName;
                if (matrixFilter === 'ASPECT') return (triPillar.vessel.inboundDrishti || []).some(a => a.planet === p.name);
                if (matrixFilter === 'BENEFIC') return ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(p.name);
                return true;
              });

              const ascLordName = chartData.planets.find(p => p.house === 1)?.signLord || 'Sun';

              return (
                <div className="pt-2">
                  <PlanetaryBhaveshMatrixView 
                    kundali={kundali} 
                    lang={activeLang} 
                    onSelectHouse={onSelectHouse} 
                  />
                </div>
              );
            })()}
          </div>
        )}

          {/* TAB 2: ADVANCED VEDIC METRICS */}
          {activeTab === 'ADVANCED' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-1 text-xs">
              {/* Bhavat Bhavam Evaluation */}
              <div className="bg-astro-card/70 border border-astro-border rounded-xl p-3.5 space-y-2.5">
                <div className="flex items-center justify-between border-b border-astro-border pb-2">
                  <div className="flex items-center gap-1.5">
                    <Compass className="w-4 h-4 text-astro-primary" />
                    <span className="font-bold text-astro-primary uppercase tracking-wider text-[11px] font-serif">
                      Bhavat Bhavam
                    </span>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                    advancedVedicMetrics.bhavatBhavam.reflectedHouseStrength.includes('Affirmative')
                      ? 'bg-emerald-100 text-emerald-900'
                      : advancedVedicMetrics.bhavatBhavam.reflectedHouseStrength.includes('Compromised')
                      ? 'bg-rose-100 text-rose-900'
                      : 'bg-astro-secondary/15 text-astro-secondary'
                  }`}>
                    {advancedVedicMetrics.bhavatBhavam.reflectedHouseStrength}
                  </span>
                </div>

                <div className="space-y-2 text-[11px]">
                  <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1">
                    <div className="flex justify-between">
                      <span className="text-astro-secondary-text">Reflected House:</span>
                      <strong className="text-astro-primary">
                        House {advancedVedicMetrics.bhavatBhavam.reflectedHouseNumber} ({advancedVedicMetrics.bhavatBhavam.reflectedHouseSign})
                      </strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-astro-secondary-text">Reflected Lord:</span>
                      <span className="font-semibold text-astro-text">
                        {advancedVedicMetrics.bhavatBhavam.reflectedLord} in H{advancedVedicMetrics.bhavatBhavam.reflectedLordHouse}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-astro-secondary-text">Lord Dignity:</span>
                      <span className="text-[10px] font-medium text-astro-primary">
                        {advancedVedicMetrics.bhavatBhavam.reflectedLordDignity}
                      </span>
                    </div>
                  </div>

                  <p className="text-[10px] text-astro-secondary-text leading-relaxed italic bg-astro-bg p-2 rounded-lg border border-astro-border">
                    {advancedVedicMetrics.bhavatBhavam.synthesisNote}
                  </p>
                </div>
              </div>

              {/* Argala & Virodhargala Matrix */}
              <div className="bg-astro-card/70 border border-astro-border rounded-xl p-3.5 space-y-2.5">
                <div className="flex items-center justify-between border-b border-astro-border pb-2">
                  <div className="flex items-center gap-1.5">
                    <Scale className="w-4 h-4 text-astro-primary" />
                    <span className="font-bold text-astro-primary uppercase tracking-wider text-[11px] font-serif">
                      Argala / Virodha Matrix
                    </span>
                  </div>
                  <span className="text-[10px] text-astro-secondary-text">Parashara Rules</span>
                </div>

                <div className="space-y-1.5 text-[10px]">
                  {advancedVedicMetrics.argalaMatrix.map((arg, idx) => (
                    <div key={idx} className="p-2 bg-white rounded-lg border border-astro-border space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-astro-primary">{arg.argalaType}</span>
                        <span className={`px-1.5 py-0.2 rounded font-semibold ${
                          arg.netInterventionVerdict.includes('Unobstructed Benefic')
                            ? 'bg-emerald-100 text-emerald-800'
                            : arg.netInterventionVerdict.includes('Unobstructed Malefic')
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-astro-card text-astro-secondary-text'
                        }`}>
                          {arg.netInterventionVerdict}
                        </span>
                      </div>
                      <div className="flex justify-between text-astro-secondary-text">
                        <span>Causing: {arg.planetsCausing.length > 0 ? arg.planetsCausing.join(', ') : 'None'} (H{arg.houseNumber})</span>
                        <span>Virodha (H{arg.opposingVirodhaHouse}): {arg.obstructingPlanets.length > 0 ? arg.obstructingPlanets.join(', ') : 'Free'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Varga / Navamsha Confirmations */}
              <div className="bg-astro-card/70 border border-astro-border rounded-xl p-3.5 space-y-2.5">
                <div className="flex items-center justify-between border-b border-astro-border pb-2">
                  <div className="flex items-center gap-1.5">
                    <Award className="w-4 h-4 text-astro-primary" />
                    <span className="font-bold text-astro-primary uppercase tracking-wider text-[11px] font-serif">
                      Varga & D9 Backing
                    </span>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-astro-accent">
                    {advancedVedicMetrics.vargaConfirmations.ashtakavargaBAVPoints} Bindus
                  </span>
                </div>

                <div className="space-y-2 text-[11px]">
                  <div className="p-2 bg-white rounded-lg border border-astro-border space-y-1">
                    <div className="flex justify-between">
                      <span className="text-astro-secondary-text">D9 Navamsha Sign:</span>
                      <strong className="text-astro-primary">{advancedVedicMetrics.vargaConfirmations.d9NavamshaSign}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-astro-secondary-text">Navamsha Lord:</span>
                      <span className="font-semibold text-astro-text">{advancedVedicMetrics.vargaConfirmations.d9NavamshaLord}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-astro-secondary-text">D9 Dignity:</span>
                      <span className="font-medium text-astro-primary text-[10px]">{advancedVedicMetrics.vargaConfirmations.d9Dignity}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                    <div className={`p-1.5 rounded-lg border text-center ${advancedVedicMetrics.vargaConfirmations.isPushkarNavamsha ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-bold' : 'bg-white border-astro-border text-astro-secondary-text'}`}>
                      <span>Pushkar Navamsha:</span>
                      <span className="block">{advancedVedicMetrics.vargaConfirmations.isPushkarNavamsha ? 'Active (Auspicious)' : 'None'}</span>
                    </div>
                    <div className={`p-1.5 rounded-lg border text-center ${advancedVedicMetrics.vargaConfirmations.isVargottama ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-bold' : 'bg-white border-astro-border text-astro-secondary-text'}`}>
                      <span>Vargottama:</span>
                      <span className="block">{advancedVedicMetrics.vargaConfirmations.isVargottama ? 'Yes (Anchored)' : 'No'}</span>
                    </div>
                  </div>

                  <div className="p-2 bg-astro-bg rounded-lg border border-astro-border text-[10px] text-astro-secondary-text">
                    <span className="font-bold text-astro-primary block uppercase">BAV Evaluation:</span>
                    <span>{advancedVedicMetrics.vargaConfirmations.bavEvaluation}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: SYNTHESIS & ACTIVATION */}
          {activeTab === 'SYNTHESIS' && (
            <div className="space-y-3 pt-1 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Manifestation Promise: Strengths vs Bottlenecks */}
                <div className="p-3.5 bg-emerald-50/60 rounded-xl border border-emerald-200 space-y-2">
                  <span className="text-[10px] font-bold uppercase text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                    Core Manifestation Strengths
                  </span>
                  <ul className="space-y-1.5 pl-4 list-disc text-[11px] text-emerald-950">
                    {synthesisAndActivation.manifestationPromise.coreStrengths.map((str, idx) => (
                      <li key={idx} className="leading-relaxed">{str}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-3.5 bg-astro-secondary-surface/60 rounded-xl border border-[#E2DED5] space-y-2">
                  <span className="text-[10px] font-bold uppercase text-astro-secondary flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-[#52635F]" />
                    Latent Bottlenecks & Karmic Demands
                  </span>
                  <ul className="space-y-1.5 pl-4 list-disc text-[11px] text-[#263633]">
                    {synthesisAndActivation.manifestationPromise.latentBottlenecks.map((bot, idx) => (
                      <li key={idx} className="leading-relaxed">{bot}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Timing Triggers & Transits */}
              <div className="p-3.5 bg-astro-card rounded-xl border border-astro-border space-y-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-astro-primary">
                  <Activity className="w-3.5 h-3.5 text-astro-accent" />
                  Dasha & Gochar Activation Windows
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-[11px]">
                  <div className="p-2.5 bg-white rounded-lg border border-astro-border space-y-1">
                    <span className="text-[10px] font-bold uppercase text-astro-secondary-text block">Dasha Correlation:</span>
                    <p className="text-astro-primary font-semibold">
                      {synthesisAndActivation.activationTriggers.activeDashaCorrelation}
                    </p>
                  </div>

                  <div className="p-2.5 bg-white rounded-lg border border-astro-border space-y-1">
                    <span className="text-[10px] font-bold uppercase text-emerald-800 block">Favorable Transits:</span>
                    <ul className="text-astro-text text-[10px] space-y-0.5">
                      {synthesisAndActivation.activationTriggers.favorableTransits.map((ft, i) => (
                        <li key={i}>• {ft}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-2.5 bg-white rounded-lg border border-astro-border space-y-1">
                    <span className="text-[10px] font-bold uppercase text-astro-secondary block">Vigilant Transits:</span>
                    <ul className="text-astro-text text-[10px] space-y-0.5">
                      {synthesisAndActivation.activationTriggers.vulnerableTransits.map((vt, i) => (
                        <li key={i}>• {vt}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Classical Parashari Remedies */}
              <div className="p-3 bg-astro-primary/5 rounded-xl border border-astro-primary/20 text-[11px] text-astro-text space-y-1.5">
                <span className="font-bold text-astro-primary flex items-center gap-1.5 uppercase text-[10px]">
                  <BookOpen className="w-3.5 h-3.5" />
                  Targeted Parashari Bhava Remediation
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[10px]">
                  {synthesisAndActivation.classicalRemedies.map((rem, i) => (
                    <div key={i} className="p-2 bg-white/90 rounded-lg border border-astro-primary/20 text-astro-text">
                      {rem}
                    </div>
                  ))}
                </div>
              </div>

              {/* Pratyantar & Dasha House Activation Engine (Parashari Timing & Logic Breakdown) */}
              {(() => {
                const now = Date.now();
                const getSubList = (node: any) => node?.antardashas || node?.subDashas || [];
                const getNodePlanet = (node: any) => node?.planet || node?.graha;

                const mdRaw = kundali.dashas?.find((d: any) => d.isCurrent) || 
                              kundali.dashas?.find((d: any) => {
                                const s = d.startDate ? new Date(d.startDate).getTime() : 0;
                                const e = d.endDate ? new Date(d.endDate).getTime() : Infinity;
                                return now >= s && now <= e;
                              }) || 
                              kundali.dashas?.[0];

                const adList = getSubList(mdRaw);
                const adRaw = adList.find((a: any) => a.isCurrent) || 
                              adList.find((a: any) => {
                                const s = a.startDate ? new Date(a.startDate).getTime() : 0;
                                const e = a.endDate ? new Date(a.endDate).getTime() : Infinity;
                                return now >= s && now <= e;
                              }) || 
                              adList[0];

                const pdList = getSubList(adRaw);
                const pdRaw = pdList.find((p: any) => p.isCurrent) || 
                              pdList.find((p: any) => {
                                const s = p.startDate ? new Date(p.startDate).getTime() : 0;
                                const e = p.endDate ? new Date(p.endDate).getTime() : Infinity;
                                return now >= s && now <= e;
                              }) || 
                              pdList[0];

                const currentMD = mdRaw ? { ...mdRaw, planet: getNodePlanet(mdRaw) } : null;
                const currentAD = adRaw ? { ...adRaw, planet: getNodePlanet(adRaw) } : null;
                const currentPD = pdRaw ? { ...pdRaw, planet: getNodePlanet(pdRaw) } : null;

                const ascIndex = chartData.ascendantIndex ?? 0;
                const signIndex = (ascIndex + houseNumber - 1) % 12;
                const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
                const signLord = ZODIAC_LORDS[signIndex];
                const occupants = (chartData.planets || []).filter((p: any) => p.house === houseNumber).map((p: any) => p.name);
                const inboundDrishti = triPillar?.vessel?.inboundDrishti || [];
                const aspectingPlanets = inboundDrishti.map((d: any) => d.planet);
                
                const NATURAL_KARAKAS: Record<number, string> = {
                  1: 'Sun', 2: 'Jupiter', 3: 'Mars', 4: 'Moon', 5: 'Jupiter', 6: 'Saturn',
                  7: 'Venus', 8: 'Saturn', 9: 'Jupiter', 10: 'Sun', 11: 'Jupiter', 12: 'Saturn'
                };

                const HOUSE_THEMES: Record<number, { titleEn: string; titleHi: string; domainEn: string; domainHi: string }> = {
                  1: { titleEn: 'Self, Vitality & Core Identity', titleHi: 'लग्न, व्यक्तित्व एवं ऊर्जा', domainEn: 'Personal initiative, physical vitality, and self-projection', domainHi: 'व्यक्तिगत पहल, शारीरिक ऊर्जा और आत्म-प्रकटन' },
                  2: { titleEn: 'Wealth, Speech & Family Values', titleHi: 'धन, वाणी एवं कुटुंब', domainEn: 'Financial resources, speech cadence, and accumulated assets', domainHi: 'वित्तीय संसाधन, वाणी और संचित संपत्ति' },
                  3: { titleEn: 'Courage, Skills & Siblings', titleHi: 'पराक्रम, कौशल एवं सहोदर', domainEn: 'Short journeys, personal courage, initiatives, and skills', domainHi: 'छोटी यात्राएं, साहस, पहल और कौशल' },
                  4: { titleEn: 'Home, Subconscious & Emotional Roots', titleHi: 'सुख, मानसिक शांति एवं अवचेतन', domainEn: 'Inner peace, emotional sensitivity, home environment, and subconscious patterns', domainHi: 'मानसिक शांति, भावनात्मक संवेदनशीलता, घरेलू माहौल और अवचेतन पैटर्न' },
                  5: { titleEn: 'Intellect, Creativity & Purva Punya', titleHi: 'बुद्धि, संतान एवं पूर्व पुण्य', domainEn: 'Higher intelligence, creative expression, speculation, and merit', domainHi: 'उच्च बुद्धि, रचनात्मक अभिव्यक्ति, संतान और पूर्व पुण्य' },
                  6: { titleEn: 'Service, Health & Obstacles', titleHi: 'रोग, ऋण एवं शत्रु (सेवा)', domainEn: 'Daily routines, health management, competition, and overcoming obstacles', domainHi: 'दैनिक कार्य, स्वास्थ्य प्रबंधन, प्रतिस्पर्धा और बाधाओं पर विजय' },
                  7: { titleEn: 'Partnerships, Marriage & Public', titleHi: 'विवाह, साझेदारी एवं जनसंपर्क', domainEn: 'Spouse, business partnerships, public interactions, and open contracts', domainHi: 'जीवनसाथी, व्यावसायिक साझेदारी, जनसंपर्क और अनुबंध' },
                  8: { titleEn: 'Transformation, Longevity & Hidden', titleHi: 'आयु, गुप्त रहस्य एवं रूपांतरण', domainEn: 'Deep psychological transformation, occult, sudden gains/losses, and longevity', domainHi: 'गहन मनोवैज्ञानिक परिवर्तन, गुप्त विद्या, आकस्मिक लाभ/हानि और आयु' },
                  9: { titleEn: 'Higher Wisdom, Fortune & Dharma', titleHi: 'भाग्य, उच्च ज्ञान एवं धर्म', domainEn: 'Higher philosophy, long-distance travel, spiritual fortune, and mentors', domainHi: 'उच्च दर्शन, लंबी यात्राएं, आध्यात्मिक भाग्य और गुरु' },
                  10: { titleEn: 'Career, Status & Public Action', titleHi: 'कर्म, करियर एवं सामाजिक प्रतिष्ठा', domainEn: 'Professional aspirations, career milestones, authority, and public recognition', domainHi: 'व्यावसायिक आकांक्षाएं, करियर के मील के पत्थर, अधिकार और प्रतिष्ठा' },
                  11: { titleEn: 'Gains, Networks & Aspirations', titleHi: 'लाभ, सामाजिक नेटवर्क एवं इच्छाएं', domainEn: 'Financial gains, friendship circles, fulfillment of desires, and net networks', domainHi: 'आर्थिक लाभ, मित्र मंडली, इच्छाओं की पूर्ति और नेटवर्क' },
                  12: { titleEn: 'Liberation, Spirituality & Expenses', titleHi: 'व्यय, मोक्ष एवं आध्यात्मिक मुक्ति', domainEn: 'Spiritual liberation, subconscious release, foreign lands, and isolation', domainHi: 'आध्यात्मिक मुक्ति, अवचेतन विमोचन, विदेश यात्रा और एकांत' },
                };

                const PLANET_FLAVORS: Record<string, { flavorEn: string; flavorHi: string }> = {
                  Sun: { flavorEn: 'Brings authority, visibility, and focused ego illumination to this domain.', flavorHi: 'इस क्षेत्र में अधिकार, दृश्यता और केंद्रित ऊर्जा लाता है।' },
                  Moon: { flavorEn: 'Infuses emotional depth, fluctuation, subconscious sensitivity, and receptivity.', flavorHi: 'भावनात्मक गहराई, संवेदनशीलता और अवचेतन तरंगें उत्पन्न करता है।' },
                  Mars: { flavorEn: 'Triggers action, drive, competitive push, and decisive energy.', flavorHi: 'कार्यवाही, गतिशीलता, प्रतिस्पर्धी जोर और निर्णायक ऊर्जा को सक्रिय करता है।' },
                  Mercury: { flavorEn: 'Brings analytical thinking, communication, coordination, and learning.', flavorHi: 'विश्लेषणात्मक सोच, संवाद, समन्वय और सीखने की प्रवृत्ति को बढ़ाता है।' },
                  Jupiter: { flavorEn: 'Expands wisdom, optimism, protective grace, and growth opportunities.', flavorHi: 'ज्ञान, आशावाद, सुरक्षात्मक कृपा और विकास के अवसर प्रदान करता है।' },
                  Venus: { flavorEn: 'Brings comfort, relational harmony, aesthetic pleasure, and desires.', flavorHi: 'सुख-सुविधा, संबंधीय सामंजस्य, सौंदर्य और आनंद की इच्छाओं को जगाता है।' },
                  Saturn: { flavorEn: 'Brings karmic discipline, structural tests, delays, and enduring responsibility.', flavorHi: 'कर्मिक अनुशासन, संरचनात्मक परीक्षा, विलंब और दीर्घकालिक जिम्मेदारी लाता है।' },
                  Rahu: { flavorEn: 'Amplifies ambition, unconventional drives, obsessions, and sudden shifts.', flavorHi: 'महकांक्षा, अपरंपरागत प्रवृत्तियों, मोह और अचानक बदलावों को तीव्र करता है।' },
                  Ketu: { flavorEn: 'Triggers spiritual detachment, sudden intuition, subtle insights, and letting go.', flavorHi: 'आध्यात्मिक वैराग्य, अंतर्ज्ञान, सूक्ष्म अंतर्दृष्टि और त्याग की भावना जगाता है।' }
                };

                const currentTheme = HOUSE_THEMES[houseNumber] || HOUSE_THEMES[1];
                const activePDPlanet = currentPD?.planet;
                const activeFlavor = activePDPlanet ? PLANET_FLAVORS[activePDPlanet] : null;

                const evalPlanet = (planetName: string) => {
                  if (!planetName) return null;
                  const isLord = signLord === planetName;
                  const isOccupant = occupants.includes(planetName);
                  const isAspecting = aspectingPlanets.includes(planetName);
                  const isKaraka = NATURAL_KARAKAS[houseNumber] === planetName;
                  const reasons: string[] = [];
                  if (isLord) reasons.push(isHi ? `भावेश (Lord of H${houseNumber})` : `Sign Lord of House ${houseNumber}`);
                  if (isOccupant) reasons.push(isHi ? `भाव स्थित (Occupant)` : `Seated in House ${houseNumber}`);
                  if (isAspecting) reasons.push(isHi ? `दृष्टिकोण सक्रियक (Aspecting)` : `Casts Drishti on House ${houseNumber}`);
                  if (isKaraka) reasons.push(isHi ? `नैसर्गिक कारक (Karaka)` : `Natural Karaka for House ${houseNumber}`);
                  return { planetName, isLord, isOccupant, isAspecting, isKaraka, isActive: reasons.length > 0, reasons };
                };

                const mdEval = evalPlanet(currentMD?.planet);
                const adEval = evalPlanet(currentAD?.planet);
                const pdEval = evalPlanet(currentPD?.planet);

                return (
                  <div className="p-4 bg-gradient-to-br from-amber-50/80 to-amber-100/40 rounded-xl border border-amber-300/80 space-y-3 text-xs">
                    <div className="flex items-center justify-between border-b border-amber-200 pb-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-700" />
                        <div>
                          <h4 className="font-bold text-amber-950 uppercase tracking-wider text-[11px] font-serif">
                            {isHi ? 'विंशोत्तरी दशा, अंतरदशा एवं प्रत्यंतर दशा भाव सक्रियक विश्लेषण' : 'Pratyantar & Dasha House Activation Engine'}
                          </h4>
                          <span className="text-[10px] text-amber-800 font-medium">
                            Domain: {isHi ? currentTheme.titleHi : currentTheme.titleEn}
                          </span>
                        </div>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-200 text-amber-900 border border-amber-300 font-mono">
                        House {houseNumber} Trigger
                      </span>
                    </div>

                    <p className="text-[11px] text-amber-950 leading-relaxed">
                      {isHi ? (
                        <span>
                          <strong className="font-semibold">भाव क्षेत्र एवं सक्रियक नियम:</strong> यह भाव मुख्य रूप से <em className="underline">{currentTheme.domainHi}</em> से संबंधित है। जब महादशा (MD), अंतर्दशा (AD) और प्रत्यंतर (PD) स्वामी इस भाव से जुड़ते हैं, तो इसके परिणाम प्रकट होते हैं।
                        </span>
                      ) : (
                        <span>
                          <strong className="font-semibold">Bhava Domain & Timing:</strong> This house governs <em className="underline">{currentTheme.domainEn}</em>. When Mahadasha (MD), Antardasha (AD), and Pratyantar (PD) lords connect through lordship, occupancy, or aspect, this life area becomes actively triggered.
                        </span>
                      )}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                      {/* Mahadasha */}
                      <div className={`p-3 rounded-lg border ${mdEval?.isActive ? 'bg-white border-emerald-300 shadow-2xs' : 'bg-white/60 border-amber-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold uppercase text-astro-secondary-text">Mahadasha (MD)</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${mdEval?.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-astro-card text-astro-secondary-text'}`}>
                            {mdEval?.isActive ? (isHi ? 'सक्रिय (Active)' : 'Activating') : (isHi ? 'तटस्थ (Neutral)' : 'Background')}
                          </span>
                        </div>
                        <div className="font-bold text-astro-primary text-sm">
                          {currentMD?.planet ? getPlanetFullName(currentMD.planet, activeLang) : 'None'}
                        </div>
                        <div className="text-[10px] text-astro-text mt-1 space-y-0.5">
                          {mdEval?.reasons.length ? mdEval.reasons.map((r, idx) => <div key={idx} className="text-emerald-900 font-medium">• {r}</div>) : <div className="text-astro-secondary-text italic">Indirect macro support</div>}
                        </div>
                      </div>

                      {/* Antardasha */}
                      <div className={`p-3 rounded-lg border ${adEval?.isActive ? 'bg-white border-emerald-300 shadow-2xs' : 'bg-white/60 border-amber-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold uppercase text-astro-secondary-text">Antardasha (AD)</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${adEval?.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-astro-card text-astro-secondary-text'}`}>
                            {adEval?.isActive ? (isHi ? 'सक्रिय (Active)' : 'Activating') : (isHi ? 'तटस्थ (Neutral)' : 'Background')}
                          </span>
                        </div>
                        <div className="font-bold text-astro-primary text-sm">
                          {currentAD?.planet ? getPlanetFullName(currentAD.planet, activeLang) : 'None'}
                        </div>
                        <div className="text-[10px] text-astro-text mt-1 space-y-0.5">
                          {adEval?.reasons.length ? adEval.reasons.map((r, idx) => <div key={idx} className="text-emerald-900 font-medium">• {r}</div>) : <div className="text-astro-secondary-text italic">Secondary structural conduit</div>}
                        </div>
                      </div>

                      {/* Pratyantar Dasha */}
                      <div className={`p-3 rounded-lg border ${pdEval?.isActive ? 'bg-white border-amber-400 shadow-2xs' : 'bg-white/60 border-amber-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold uppercase text-amber-900 font-bold">Pratyantar (PD - Trigger)</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${pdEval?.isActive ? 'bg-amber-500 text-white' : 'bg-astro-card text-astro-secondary-text'}`}>
                            {pdEval?.isActive ? (isHi ? 'तात्कालिक उत्प्रेरक' : 'Direct Trigger') : (isHi ? 'सामान्य' : 'Supporting')}
                          </span>
                        </div>
                        <div className="font-bold text-amber-950 text-sm">
                          {currentPD?.planet ? getPlanetFullName(currentPD.planet, activeLang) : 'None'}
                        </div>
                        <div className="text-[10px] text-astro-text mt-1 space-y-0.5">
                          {pdEval?.reasons.length ? pdEval.reasons.map((r, idx) => <div key={idx} className="text-amber-950 font-bold">• {r}</div>) : <div className="text-astro-secondary-text italic">General micro-flow</div>}
                        </div>
                      </div>
                    </div>

                    {/* Planetary Experiential Flavor Modifier */}
                    {activeFlavor && (
                      <div className="p-2.5 bg-amber-50/90 rounded-lg border border-amber-300 text-[11px] text-amber-950 flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                        <div>
                          <strong className="font-semibold">{isHi ? `प्रत्यंतर स्वामी (${currentPD?.planet}) का अनुभवजन्य प्रभाव: ` : `Pratyantar Lord (${currentPD?.planet}) Experiential Flavor: `}</strong>
                          {isHi ? activeFlavor.flavorHi : activeFlavor.flavorEn}
                        </div>
                      </div>
                    )}

                    <div className="p-2.5 bg-white rounded-lg border border-amber-200 text-[11px] text-amber-950 leading-relaxed flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                      <div>
                        <strong className="font-semibold">{isHi ? 'तार्किक निष्कर्ष: ' : 'Logical Conclusion: '}</strong>
                        {pdEval?.isActive 
                          ? (isHi ? `वर्तमान प्रत्यंतर स्वामी (${currentPD?.planet}) House ${houseNumber} (${currentTheme.titleHi}) को सक्रिय कर रहा है। ${activeFlavor?.flavorHi || ''}` : `The running Pratyantar lord (${currentPD?.planet}) directly activates House ${houseNumber} (${currentTheme.titleEn}). ${activeFlavor?.flavorEn || ''}`)
                          : (isHi ? `वर्तमान प्रत्यंतर स्वामी House ${houseNumber} के मामलों को परोक्ष रूप से प्रभावित कर रहा है; मुख्य प्रकटन महादशा और अंतर्दशा के माध्यम से हो रहा है।` : `The active Pratyantar lord connects indirectly to House ${houseNumber} (${currentTheme.titleEn}); primary fruition is governed by the operating Mahadasha and Antardasha.`)}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Actionable Practical Guidance & Practical Remedies */}
              <div className="space-y-3 pt-2">
                {/* Practical Life Guidance & Real-Life Impact */}
                <div className="p-4 bg-gradient-to-br from-indigo-50/70 to-blue-50/40 dark:bg-astro-card/80 rounded-xl border border-indigo-200 space-y-2.5">
                  <div className="flex items-center gap-2 border-b border-indigo-200/80 pb-2">
                    <BookOpen className="w-4 h-4 text-indigo-700" />
                    <h4 className="font-bold text-indigo-950 uppercase tracking-wider text-[11px] font-serif">
                      {isHi ? 'व्यावहारिक जीवन मार्गदर्शन एवं वास्तविक प्रभाव' : 'Actionable Practical Guidance & Real-Life Impact'}
                    </h4>
                  </div>
                  <div className="space-y-2 text-[11px] text-indigo-950 leading-relaxed">
                    <p><strong className="font-semibold">{isHi ? 'परिदृश्य विश्लेषण: ' : 'Practical Overview: '}</strong>{(laymanPracticalSummary as any).headline || laymanPracticalSummary.verdictTitle}</p>
                    <p><strong className="font-semibold">{isHi ? 'व्यावहारिक सलाह: ' : 'Key Guidance: '}</strong>{(laymanPracticalSummary as any).laymanAdvice || laymanPracticalSummary.netVectorSummary}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] pt-1">
                      <div className="p-2 bg-white/80 rounded-lg border border-indigo-100">
                        <span className="font-bold text-indigo-900 block">{isHi ? 'ताकत (Leverage):' : 'Strengths to Leverage:'}</span>
                        <span>{(laymanPracticalSummary as any).actionableDo || laymanPracticalSummary.drivers?.slice(0, 2).join(' ') || (isHi ? 'सकारात्मक कारकों का उपयोग करें' : 'Utilize key drivers')}</span>
                      </div>
                      <div className="p-2 bg-white/80 rounded-lg border border-indigo-100">
                        <span className="font-bold text-rose-900 block">{isHi ? 'सावधानी (Avoid):' : 'Pitfalls to Avoid:'}</span>
                        <span>{(laymanPracticalSummary as any).actionableDont || laymanPracticalSummary.frictions?.slice(0, 2).join(' ') || (isHi ? 'अति-उत्साह से बचें' : 'Avoid overextension')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Practical Karmic Remedies & Lifestyle Alignment */}
                <div className="p-4 bg-gradient-to-br from-emerald-50/70 to-teal-50/40 rounded-xl border border-emerald-200 space-y-2.5">
                  <div className="flex items-center justify-between border-b border-emerald-200/80 pb-2">
                    <div className="flex items-center gap-2">
                      <Compass className="w-4 h-4 text-emerald-700" />
                      <h4 className="font-bold text-emerald-950 uppercase tracking-wider text-[11px] font-serif">
                        {isHi ? 'व्यवहारिक एवं कर्मिक उपाय' : 'Practical Karmic Remedies & Lifestyle Alignment'}
                      </h4>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-200 text-emerald-900 border border-emerald-300">
                      {isHi ? 'कर्म संशोधन' : 'Behavioral Shift'}
                    </span>
                  </div>
                  <div className="space-y-2 text-[11px] text-emerald-950">
                    <div className="p-2.5 bg-white/80 rounded-lg border border-emerald-200">
                      <strong className="font-semibold text-emerald-900 block mb-1">{isHi ? 'कर्मिक परिवर्तन (Key Shift):' : 'Key Behavioral Shift:'}</strong>
                      <span>{(remedies as any).karmicShift || remedies.behavioral}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px]">
                      <div className="p-2 bg-white/80 rounded-lg border border-emerald-200">
                        <strong className="font-semibold text-emerald-900 block mb-1">{isHi ? 'दैनिक आदतें:' : 'Recommended Actions:'}</strong>
                        <ul className="list-disc pl-3.5 space-y-0.5">
                          {((remedies as any).practicalActions || [remedies.quickSummary]).map((act: string, idx: number) => (
                            <li key={idx}>{act}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-2 bg-white/80 rounded-lg border border-emerald-200">
                        <strong className="font-semibold text-emerald-900 block mb-1">{isHi ? 'लाइफस्टाइल एवं पर्यावरण:' : 'Lifestyle & Environment:'}</strong>
                        <p>{(remedies as any).lifestyleAdjustment || remedies.quickSummary}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sacred Vedic Upayas (Mantras, Stotram & Deity Worship) */}
                <div className="p-4 bg-gradient-to-br from-amber-50/80 to-orange-50/40 rounded-xl border border-amber-300 space-y-2.5">
                  <div className="flex items-center justify-between border-b border-amber-200 pb-2">
                    <div className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-amber-700" />
                      <h4 className="font-bold text-amber-950 uppercase tracking-wider text-[11px] font-serif">
                        {isHi ? 'वैदिक मंत्र, स्तोत्र एवं देव उपासना' : 'Sacred Vedic Upayas (Mantras & Deity Worship)'}
                      </h4>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-200 text-amber-900 border border-amber-300">
                      {isHi ? 'वैदिक साधना' : 'Vedic Sadhana'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 text-[11px]">
                    <div className="p-3 bg-white/90 rounded-lg border border-amber-200 space-y-1.5">
                      <span className="text-[10px] font-bold uppercase text-amber-900 block">{isHi ? 'बीज मंत्र:' : 'Primary Beej Mantra:'}</span>
                      <p className="font-serif font-bold text-amber-950 text-sm tracking-wide bg-amber-100/60 p-2 rounded border border-amber-200 text-center">
                        {remedies.mantra}
                      </p>
                      <p className="text-[10px] text-amber-900 italic mt-1">{(remedies as any).mantraGuidance || remedies.chantingGuidance}</p>
                    </div>

                    <div className="p-3 bg-white/90 rounded-lg border border-amber-200 space-y-1.5">
                      <span className="text-[10px] font-bold uppercase text-amber-900 block">{isHi ? 'दान एवं परोपकार:' : 'Daan & Charity:'}</span>
                      <p className="text-amber-950 text-[11px] leading-relaxed">{(remedies as any).donation || remedies.daan || (isHi ? 'सुपात्र एवं जरूरतमंदों की सेवा करें।' : 'Offer selfless service to those in need.')}</p>
                    </div>
                  </div>
                </div>

                {/* Somatic & House Daily Upayas */}
                <div className="p-4 bg-gradient-to-br from-purple-50/70 to-fuchsia-50/40 rounded-xl border border-purple-200 space-y-2.5">
                  <div className="flex items-center gap-2 border-b border-purple-200 pb-2">
                    <Sparkles className="w-4 h-4 text-purple-700" />
                    <h4 className="font-bold text-purple-950 uppercase tracking-wider text-[11px] font-serif">
                      {isHi ? 'दैनिक भाव एवं सोमैटिक (शरीर/स्थान) उपाय' : 'Somatic & House Daily Upayas'}
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px]">
                    <div className="p-2.5 bg-white/80 rounded-lg border border-purple-200">
                      <strong className="font-bold text-purple-900 block mb-1">{isHi ? 'सोमैटिक / शरीर क्रिया:' : 'Somatic / Body Practice:'}</strong>
                      <p className="text-purple-950">{(remedies as any).somaticRemedy || remedies.spiritual}</p>
                    </div>
                    <div className="p-2.5 bg-white/80 rounded-lg border border-purple-200">
                      <strong className="font-bold text-purple-900 block mb-1">{isHi ? 'घरेलू/स्थान उपाय:' : 'House & Space Remedy:'}</strong>
                      <p className="text-purple-950">{(remedies as any).houseRemedy || remedies.caution}</p>
                    </div>
                  </div>
                </div>

                {/* Gemstone & Ritual Safeguards */}
                <div className="p-3.5 bg-rose-50/70 rounded-xl border border-rose-200 space-y-2">
                  <div className="flex items-center justify-between border-b border-rose-200 pb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-700" />
                      <h4 className="font-bold text-rose-950 uppercase tracking-wider text-[11px] font-serif">
                        {isHi ? 'रत्न एवं सुरक्षा सावधानियां' : 'Gemstone & Ritual Safeguards'}
                      </h4>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-200 text-rose-900 border border-rose-300">
                      {isHi ? 'सावधानी' : 'Caution'}
                    </span>
                  </div>
                  <div className="p-2.5 bg-white/90 rounded-lg border border-rose-200 text-[11px] text-rose-950">
                    <p><strong className="font-semibold">{isHi ? 'रत्न संबंधी निर्देश: ' : 'Gemstone Guidance: '}</strong>{remedies.gemstoneCaution}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
